<template>
  <!-- Horní navigační lišta -->
  <v-app-bar app color="primary" v-if="isMobile">
    <v-app-bar-title>Obsah</v-app-bar-title>
    <v-spacer></v-spacer>
    
    <!-- Mobilní menu - tři tečky -->
    <v-menu transition="slide-y-transition" eager close-on-content-click="false">
      <template v-slot:activator="{ props }">
        <v-btn icon v-bind="props">
          <v-icon>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>
      <v-card width="250">
        <v-list>
          <v-expansion-panels :key="searchQuery">
            <v-expansion-panel
              v-for="(category, i) in filteredTopics"
              :key="i"
            >
              <v-expansion-panel-title @click.stop>{{ category.title }}</v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-list density="compact">
                  <v-list-item
                    v-for="(subtopic, j) in category.subtopics"
                    :key="j"
                    @click="selectTopic(subtopic.key)"
                    :class="{ active: selectedTopicKey === subtopic.key }"
                  >
                    <v-list-item-title class="text-body-2">{{ subtopic.title }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-list>
        <v-text-field
          v-model="searchQuery"
          label="Hledat v obsahu..."
          prepend-inner-icon="mdi-magnify"
          clearable
          variant="outlined"
          density="compact"
          class="search-field, ma-1"
          @click.stop
          hide-details="true"
        />
      </v-card>
    </v-menu>
  </v-app-bar>


  <!-- Levé menu pro desktop -->
  <v-navigation-drawer v-if="!isMobile" v-model="drawer" permanent>
    <v-list>
      <v-expansion-panels variant="accordion">
        <v-expansion-panel
          v-for="(category, i) in filteredTopics"
          :key="i"
        >
          <v-expansion-panel-title>{{ category.title }}</v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-list density="compact">
              <v-list-item
                v-for="(subtopic, j) in category.subtopics"
                :key="j"
                @click="selectTopic(subtopic.key)"
                :class="{ active: selectedTopicKey === subtopic.key }"
              >
                <v-list-item-title class="text-body-2">{{ subtopic.title }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-list>
    <v-text-field
      v-model="searchQuery"
      label="Hledat v obsahu..."
      prepend-inner-icon="mdi-magnify"
      clearable
      variant="outlined"
      density="compact"
      class="search-field, ma-1"
      hide-details="true"
    />
  </v-navigation-drawer>

  <!-- Pravý obsah -->
    <v-container>
  <v-card>
    <v-card-title>{{ getTopicTitle }}</v-card-title>

    <v-card-text>
      <!-- Výsledky hledání (když je něco napsáno) -->
      <template v-if="isSearching">
        <div class="text-subtitle-2 mb-2">
          Výsledky hledání: „{{ searchQuery.trim() }}“
        </div>

        <v-list density="compact" class="mb-4">
          <v-list-item v-if="searchResults.length === 0">
            <v-list-item-title>Nic nenalezeno.</v-list-item-title>
          </v-list-item>

          <v-list-item
            v-for="hit in searchResults"
            :key="hit.key"
            @click="openSearchResult(hit.key)"
            :class="{ active: selectedTopicKey === hit.key }"
          >
            <v-list-item-title v-html="hit.titleHtml" />
            <v-list-item-subtitle v-html="hit.snippetHtml" />
          </v-list-item>
        </v-list>

        <v-divider :thickness="10" color="primary" class="mb-4 border-opacity-75" variant="double"
        gradient />
      </template>

      <!-- Vlastní článek (vždy) -->
      <template v-if="currentComponent">
        <component :is="currentComponent" />
      </template>
      <template v-else>
        <div v-html="highlightedContent"></div>
      </template>
    </v-card-text>
  </v-card>
</v-container>

</template>
    
<script setup>
import { ref, computed, onMounted, onUnmounted, watch, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import HelpPageAPI from "@/components/HelpPageAPI.vue"
import HelpPageFAQ from "@/components/HelpPageFAQ.vue"


const selectedTopicKey = ref(null);
// Funkce pro výběr tématu a zavření menu po výběru
const selectTopic = (key) => {
  selectedTopicKey.value = key;
};

const router = useRouter();
const route = useRoute();

// base url adresa serveru, odkud se načítají statické obrázky
const BaseUrl = ref(process.env.VUE_APP_API_STATIC_URL);

// Stav levého menu a výběr tématu
const drawer = ref(true);

// Mapování, které při klíči "dev-api" vrátí komponentu ApiHelp
const topicComponents = {
  "dev-api": HelpPageAPI,
  // Pokud budete mít další témata, přidáte je sem stejným způsobem.
};
const currentComponent = computed(() => {
  if (!selectedTopicKey.value) return HelpPageFAQ
    return topicComponents[selectedTopicKey.value] || null
});


// Seznam témat nápovědy
const helpTopics = ref([
  {
    title: "Registrace do robisu",
    subtopics: [
      { title: "Registrace oddílu", key: "reg-clubs" },
      { title: "Registrace účtů", key: "reg-account" },
      { title: "Pravomoce uživatelů", key: "reg-permmissions" }
    ]
  },
  {
    title: "Vše k soutěži",
    subtopics: [
      { title: "Vytvoření soutěže", key: "event-create" },
      { title: "Přihlášení na soutěž", key: "event-entry" },
      { title: "Hromadné přihlašování", key: "event-bulk-entry" },
      { title: "Přihlaš. organizátorů", key: "event-organizers-entry" },
      { title: "Editace soutěže", key: "event-edit" },
      { title: "Přidání etapy", key: "event-etap-create" },
      { title: "Správa kategorií", key: "event-category" },
      { title: "Startovné", key: "event-starting-fee" },
      { title: "Správci soutěže", key: "event-editors" },
      { title: "Zrušení soutěže", key: "event-canceled" },
      { title: "Volba startu", key: "event-start-choice" },
      { title: "Body na mapě", key: "event-map" },
      { title: "Editace přihlášených", key: "event-entry-edit" },
      { title: "Ubytování a strava", key: "event-food-acc" },
      { title: "Služby", key: "event-services" },
      { title: "Přehled plateb", key: "event-payments" },
      { title: "Činovníci a organizátoři", key: "event-workers" },
      { title: "API klíč etapy", key: "event-apikey" },
      { title: "Nahrání start. a výs.", key: "event-startlist-results" },
      { title: "Výsledky pro komentátora", key: "commentator-results" },
      { title: "Protokol", key: "event-protocol" },
      { title: "Pravomoce", key: "event-permissions" },
      { title: "Seznam XLS", key: "event-xls" },
      { title: "Automodul", key: "event-automodule" },
      { title: "Livelox", key: "event-livelox" },
      { title: "Omluvenky", key: "event-absences" },
      { title: "Uzavření soutěže", key: "event-close" }


    ]
  },
  {
    title: "Oddílová sekce",
    subtopics: [
      { title: "Přidání člena", key: "reg-account" },
      { title: "Správa členů", key: "club-members" },
      { title: "Pravidla financování", key: "club-payment-rules" },
      { title: "Účetnictví", key: "club-payments" },
      { title: "Správa automodulů", key: "club-automodules" },

    ]
  },
  {
    title: "Ostatní",
    subtopics: [
      { title: "Rozhodčí a trenéři", key: "other-referee-coach" },
      { title: "Žebříčky", key: "other-ranking" },
      { title: "Výkonnostní třídy", key: "other-vt" },
      { title: "Světový Ranking", key: "other-global-ranking" },
      { title: "Radiokontroly", key: "other-oracon" },
      { title: "O-checklist", key: "other-ochecklist" },





    ]
  },

  {
    title: "Pro vývojáře",
    subtopics: [
      { title: "Obecné informace", key: "dev-info" },
      { title: "SI Droid Event", key: "dev-sidroid" },
      { title: "Přehled přihlášených", key: "dev-entries" },
      { title: "Startovka (import)", key: "dev-startlist" },
      { title: "Výsledky (import)", key: "dev-results" },
      { title: "Online výsledky", key: "dev-online-results" },
      { title: "API", key: "dev-api" },

    ]
  }
]);

// Mapování klíčů na obsah
const topicContent = {


  "reg-clubs": {
    title: "Jak registrovat oddíl",
    content: `<p>Nový oddíl může být vytvořen pouze administrátorem ROBisu. Oddíl může být i ze zahraničí. K založení je potřeba název oddílu a údaje předsedy oddílu, jehož účet administrátor založí spolu s oddílem. Předseda dostane všechny pravomoce na správu oddílu a členy si registruje sám, viz <a href="#" data-topic="reg-account">registrace účtů</a>.<br>
    Kontakt na administrátora ROBisu: info@rob-is.cz</p>
    `
  },


  "reg-account": {
    title: "Jak registrovat účet / přidat člena do oddílu",
    content: `
        <p>Registrovat nové uživatele může pouze správce oddílu a to na stránce svého oddílu v záložce Členové.<br>
          <a href="${BaseUrl.value}static/help/reg_button.jpg" target="_blank"><img src="${BaseUrl.value}static/help/reg_button.jpg" alt="registration icon" width="250px"></a><br>
          Otevře se dialogové okno, které má dvě části. V první části je třeba zkontrolovat, zda daný uživatel už účet v ROBisu nemá. Pokud ano, vybere se. Pokud uživatel ve stejném oddíle někdy v minulosti byl, všechny údaje se mu předvyplní (včetně indexu) podle původních hodnot. Pokud uživatel v databázi nebyl nalezen, pokračuje se do druhé části, kde se uživatel zaregistruje.<br>
          <a href="${BaseUrl.value}static/help/account_register_dialog.png" target="_blank"><img src="${BaseUrl.value}static/help/account_register_dialog.png" alt="registration dialog" width="700px"></a><br>
          Pro registraci kompletní členské základny je vhodná hromadná registrace. Na každý řádek je nutné vložit povinné parametry přesně ve formátu: Příjmení;Jméno;index;email;rok narození;pohlaví;číslo SI. Pohlaví nabývá hodnot: <b>M</b> - muž, <b>F</b> - žena. <b>Email</b> a <b>číslo SI</b> nejsou povinné. Např. Novotný;Petr;XXX8501;;1985;M;. Zde není vyplněn email a poslední parametr (číslo SI) není uveden, člen se tedy registruje bez emailu a čísla SI. <b>Pokud uživatel nemá vyplněný email, nemůže se do ROBisu přihlásit!</b> Oddělení parametrů musí být středníkem. Nejlépe je tato data připravit v Excelu. Každému uživateli bez vyplněného emailu se při registraci automaticky vygeneruje email ve tvaru index@rob-is.cz, protože ROBis neumí pracovat s uživatelem bez emailu.</p>
        <p>
          Pokud <b>mladší členové</b> nemají email, není třeba jej vyplňovat. Email je pro každý účet unikátní, a proto nelze vyplnit email rodičů, kteří mají v ROBisu také svůj účet. Pokud rodiče účet v ROBisu nemají, lze jej samozřejmě vyplnit. Na účet bez emailu se nebude možné přihlásit, protože heslo si uživatel vytváří právě přes email. <b>Předseda oddílu</b> však může přes správu členů v oddíle <b>nastavit pro tyto uživatele účet</b>, který bude možné přes něj v ROBisu na soutěže přihlašovat. Takto mohou rodiče přes sebe přihlašovat své děti. Jakmile si mladý člen založí svůj email, předseda oddílu tento email uživateli zapíše a bude se moci na účet běžně přihlásit.<br> 
          <a href="${BaseUrl.value}static/help/club_person_assist_user_edit.png" target="_blank"><img src="${BaseUrl.value}static/help/club_person_assist_user_edit.png" width="600px" alt="přidání uživatele pro povolení editace jiného účtu"></a>
        </p>
        <p>
          Pokud při registraci <b>není vyplněný email</b>, automaticky se uživateli email vytvoří ve formátu index@rob-is.cz. Tento email však není pro uživatele použitelný a řeší pouze technické otázky v ROBISu. Předseda oddílu jej může kdykoli změnit. 
        </p>
      
    `
  },


  "reg-permmissions": {
    title: "Pravomoce uživatelů",
    content: `
          <p>Barevně jsou rozlišeny možné role uživatelů ROBisu. Stejnou funkci může mít více uživatelů.</p>
        <h3>Manažer</h3>
        <ul>
            <li>Může editovat a vytvářet třídy rozhodčích a trenérů.</li>
            <li>Může editovat všechny Žebříčky.</li>
        </ul>

        <h3>Správce online kontrol</h3>
        <ul>
            <li>Má plný přístup ke správě online kontrol ze speciální stránky.</li>
            <li>Může měnit všechny údaje pro zařízení i upravovat jednotlivé záznamy ražení.</li>
        </ul>

        <h3>Předseda oddílu</h3>
        <ul>
            <li>Určuje uživatele pro oprávnění (pravomoce) <span style="color:rgb(255, 123, 0)">organizátor</span> a <span style="color:rgb(255, 123, 0)">správce oddílu</span>.</li>
            <li>Může <b>vyloučit uživatele z oddílu</b> (odebrat jim index a příslušnost k oddílu) a <b>smazat účty</b>, které jsou mladší jak 14 dní.</li>
            <li>V oddílu může být více osob s pravomocemi, jako má předseda oddílu.</li>
        </ul>

        <h3>Správce oddílu</h3>
        <ul>
            <li>Vytváří registrace nových členů oddílu</li>
            <li>Vidí pravomoce členů ve svém oddílu a může editovat jejich údaje</li>
            <li>Může vytvářet nebo editovat přihlášky členů oddílu na soutěže</li>
            <li>Může spravovat automoduly v soutěži.</li>
        </ul>
        <h3>Organizátor</h3>
        <ul>
            <li>Zakládá soutěže pod hlavičkou svého oddílu</li>
            <li>Má přístup ke všem funkcím v soutěžích pořádaných jeho oddílem</li>
            <li>Pokud oddílu byly přiděleny radiokontroly, může je spravovat</li>
        </ul>

        `

  },
  "event-create": {
    title: "Vytvoření soutěže",
    content: `
    <p>
      Pro vytvoření nové soutěže v kalendáři klikněte na zelenou ikonku vlevo nahoře na hlavní stránce.<br>
      <a href="${BaseUrl.value}static/help/event_create.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_create.png" alt="ikona pro vytvoření soutěže" width="900px">
      </a><br>
      Je třeba vyplnit všechna pole. Pokud se jedná o závod, zvolte typ soutěže "Závod". V tomto případě je navíc nutné přidat alespoň jednu etapu, protože jinak nebude možné do ROBisu nahrát startovku ani výsledky. 
    </p>`
  },

  "event-edit": {
    title: "Jak editovat soutěž",
    content: `
    <p>
      Přístup k editaci je pro oprávněné uživatele přes stránku soutěže. V kalendáři je třeba soutěž rozkliknout a pokud je editace povolena, vpravo nahoře se zobrazí ozubené kolo. Přes něj se otevře nastavení soutěže.<br>
      <a href="${BaseUrl.value}static/help/event_edit_button.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_edit_button.png" alt="ikona pro vytvoření soutěže" width="900px">
      </a><br>
      S jakými pravomocemi a za jakých podmínek je možné soutěž upravovat, je uvedeno v tématu <a href="#" data-topic="event-permissions">Pravomoce</a>. 
    </p>`
  },

  "event-etap-create": {
    title: "Přidání etapy do vytvořené soutěže",
    content: `
    <p>
      V nastavení etapy lze přes tlačítko plus přidat další etapu. Pořadí zobrazení etap na stránce soutěže závisí na datu etapy a na čase startu 00. Etapu lze smazat pouze tehdy, pokud v ní nejsou přihlášení závodníci; po kliknutí na etapu je k dispozici tlačítko pro smazání etapy vpravo dole.<br>
      <a href="${BaseUrl.value}static/help/event_etap_create.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_etap_create.png" alt="ikona pro vytvoření etapy" width="900px">
      </a>
    </p>`
  },




  "event-category": {
    title: "Správa kategorií",
    content: `
    <p>
      V nastavení soutěže použijte modré tlačítko pro hromadnou správu kategorií (na stránce etapy: Spravovat kategorie). Pokud se kategorie spravují z hlavní stránky soutěže, lze upravovat jak kategorie pro soutěž (v přihláškách se zobrazí jako "Vyber hlavní kategorii"), tak po zaškrtnutí volby u tlačítka Uložit "přepsat i v etapách" se veškerá data kategorií přepíší i do všech etap v soutěži. Lze tak upravit kategorie najednou, například pokud je třeba zavést jen dvě. Pokud je třeba změnit kategorie pouze v určité etapě, zvolí se daná etapa a použije se tlačítko "Spravovat kategorie". <br>
      <span class="text-red">Pozor! Pokud se otevře správa kategorií ze stránky soutěže, vyplněné částky startovné a zvýšené startovné nemají žádný vliv. V etapách se aplikují pouze v případě, že se dole zvolí "přepsat i v etapách" a uloží se.</span>
    </p> 
      <br>
      <a href="${BaseUrl.value}static/help/event_category.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_category.png" alt="" width="1000px">
      </a><br>
      <p>S vytvořením soutěže se automaticky vytvoří kategorie, které jsou podle pravidel vypisované na soutěžích prvního stupně (MČR, NŽ).</p><br>
      
      <a href="${BaseUrl.value}static/help/event_category_dialog.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_category_dialog.png" alt="" width="600px">
      </a><br>

      <h3>Předvolba kategorie a název</h3>
      <p>
      Novou kategorii lze přidat přes zelené tlačítko vpravo nahoře. V tabulce se vytvoří nový řádek. V prvním sloupci se vybírá kategorie tak, jak je definovaná v pravidlech, aby se podle těchto pravidel mohl ROBis i jiné aplikace řídit. Čísla v závorce značí věk. Jedná se o standardní pravidla kromě: MDR (všechna pohlaví 0–13 let), Ostatní (všechna pohlaví 0–99 let), ORG (organizátoři) (všechna pohlaví 0–99 let). Kategorie ORG se zobrazuje všem, kteří jsou ze stejného oddílu jako pořádající oddíl, a slouží k přihlášení organizátorů pro lepší přehled. Kvůli přehlednosti se ženám nezobrazují mužské kategorie, i když to pravidla dovolují. Druhý sloupec nese název kategorie, takže lze vytvořit mužskou kategorii s názvem H s tím, že v prvním sloupci bude zvoleno M20 (0–99).
      </p>
      <h3>Konkrétní příklady kdy co zvolit</h3> 
      <p>  
      Kategorie příchozích - [Ostatní (0–99)], Příchozí<br>
      Kategorie muži, jakýkoli věk - [M20 (0–99)], H<br>
      Kategorie ženy, jakýkoli věk - [D20 (0–99)], D<br>
      Kategorie junioři a méně (lze pouze muži) - [M19 (0–19)], Mladší <br>
      Kategorie veteráni muži i ženy - [Ostatní (0-99)], Veteráni (kategorie se bohužel nabídne i mladším ročníkům MD20-)<br>
      Kategorie děti - [Ostatní (0-99)], Děti (kategorie se bohužel nabídne i starším ročníkům MD20+)<br>
      Kategorie pro přihlašování organizátorů z oddílu, jakýkoli věk a pohlaví, omezeno pouze pro pořádající oddíl - [ORG (0-99)], Organizátor<br>
      <br>
      Z technického hlediska nelze vytvořit kategorii MD16 apod., protože ROBis nekombinuje pohlaví, kromě možností Ostatní, MDR a ORG.
      </p>
      
      <h3>Startovné a zvýšené startovné</h3>
      <p>
      Startovné lze zapsat hromadně pro všechny kategorie. Využijte k tomu první řádek dialogového okna správce kategorií. Najdete tam pole "hromadné startovné", "hromadné zvýšené startovné" a "hromadné startovné soutěže". Do pole je třeba napsat číslo a poté kliknout mimo pole. Číslo se automaticky přepíše do daného sloupce u všech kategorií, pokud tam již cena nebyla zapsána v minulosti, tedy pouze do prázdných buněk. Pokud už kategorie má startovné zapsané z minulosti, cena se nezmění, aby se zabránilo nechtěnému přepisování.<br>
      <b>Startovné</b> se počítá, pokud je uživatel přihlášen v I. termínu přihlášek. <b>Zvýšené startovné</b> se počítá, pokud je závodník přihlášen ve II. termínu přihlášek. Pokud cenu za II. termín neuvedeš, ale přesto bude závodník přihlášen ve II. termínu přihlášek, automaticky se mu počítá cena uvedená v I. termínu přihlášek. Pokud neuvedeš žádnou cenu za startovné, počítá se automaticky 0 Kč.<br>
      Ve správě kategorií v hlavní soutěži lze také zadat startovné za přihlášku do "hlavní kategorie soutěže". Sloupeček "startovné soutěže". Toto lze využít ve výjimečných případech, např. pokud nemáš v soutěži žádné etapy a chceš přihlášku zpoplatnit. 
      </p>
      <h3>Vyhledávané kontroly</h3>
      <p>
      Pokud se etapa rozklikne a zvolí se tlačítko Spravovat kategorie, lze pro vybranou etapu v jednotlivých kategoriích na konci řádku vypsat kontroly, které kategorie vyhledává. Toto pole slouží pouze ke zobrazení výčtu vyhledávaných kontrol závodníkům ve výsledcích a nepodílí se na validaci dat apod. 
    </p>
    <h3>Úroveň a započítání</h3>
    <p>
     V etapách ve správě kategorií lze navíc u každé kategorie upravit soutěžní úroveň (počítá se z ní VT) a započítání do žebříčků a VT. Může se stát (např. na akademickém mistrovství), že v etapě soutěží kategorie s různými úrovněmi soutěže, např. hlavní kategorie a příchozí. I když bude příchozí kategorie označená jako M20, lze ji vyřadit z počítání VT a žebříčků odškrtnutím checkboxu. Odškrtnutí lze použít také v případě zrušeného závodu. Průměry zúčastněným závodníkům musí do žebříčku zadat uživatel s oprávněním manažer. 
    </p>

    `
  },


  "event-starting-fee": {
    title: "Startovné",
    content: `
    <p>
     Startovné je možné definovat ve správě kategorií v nastavení soutěže (modré tlačítko Spravovat kategorie hromadně, případně Spravovat kategorie). Postup je popsán v tématu <a href="#" data-topic="event-category">Správa kategorií</a>.
    </p>`
  },







  "event-editors": {
    title: "Správci soutěže",
    content: `
    <p>
      V nastavení soutěže může <b>autor</b>, který soutěž vytvořil určit, kdo přesně může soutěž editovat. Lze vybrat několik účtů z těch, které v ROBisu jsou. Pokud je pole prázdné, editovat soutěž může autor, který soutěž vytvořil a všichni, kteří mají pravomoc organizátor a jsou ze stejného oddílu, jako je pořádající oddíl. Pokud vyplníte pole správci této soutěže, automaticky se tím uzavře přístup organizátorům z oddílu. Editovat soutěž pak mají oprávnění autor soutěže a účty, které jsou uvedené v poli "správci této soutěže". Pokud chcete omezit pravomoce editace pouze na sebe, vyberte jako správce sami sebe.  
    </p>`
  },

  "event-canceled": {
    title: "Zrušení (odvolání) soutěže",
    content: `
    <p>
      V případě nenadálé situace je možné v ROBisu upozornit závodníky, že došlo ke zrušení soutěže, aniž by bylo nutné soutěž z ROBisu smazat. V takovém případě zvolte v dalším nastavení soutěže "Nastav zrušení soutěže". Zachovají se všechna data v soutěži, ale závodníkům nebude účtováno startovné, ubytování, strava ani služby.<br>
      <a href="${BaseUrl.value}static/help/event_canceled.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_canceled.png" alt="" width="500px">
      </a>
    </p>`
  },

  "event-start-choice": {
    title: "Volba startu",
    content: `
    <p>
      Tato funkce umožňuje závodníkům v přihláškách zvolit si dobu startu. <b>Možnosti: 0 - první startující, 1 - první skupina, 2 - druhá skupina, 3 - třetí skupina, 4 - poslední startující.</b> Informace o volbě závodníků se zobrazí v XLSX souboru a v některých exportovaných CSV souborech, které slouží pro import dat do vyčítacích softwarů. Jinde se tato volba neprojeví a je na rozhodnutí organizátora, zda ji využije. Pokud se neplánuje použít, je vhodné tuto volbu ponechat vypnutou.  
      <br>
      <a href="${BaseUrl.value}static/help/event_start_choice.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_start_choice.png" alt="" width="900px">
      </a>
    </p>`
  },

  "event-map": {
    title: "Body na mapě",
    content: `
    <p>
      Na stránce soutěže (ne v nastavení soutěže) je mapa zobrazující body, jako například místo shromaždiště, start, cíl, ubytování apod. Běžní uživatelé mohou kliknout na název bodu a tím spustit navigaci v Mapách Google. Uživatelé s pravomocí editovat soutěž (<a href="#" data-topic="event-permissions">viz pravomoce</a>) mohou bod vložit kliknutím levým tlačítkem myši na konkrétní místo v mapě. Spustí se dialogové okno, které vyzve k zadání popisu daného místa. Souřadnice se uloží automaticky. Ikona pro smazání je viditelná pouze pro správce soutěže.
      <br>
      <a href="${BaseUrl.value}static/help/event_map.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_map.png" alt="" width="1100px">
      </a>
    </p>`
  },

  "event-entry-edit": {
    title: "Editace přihlášených z pohledu pořadatele",
    content: `
    <p>
      V nastavení soutěže lze ve druhém menu níže přes tlačítko "Přihlásit" v sekci "Správa přihlášených" přihlásit závodníky, a to i po termínu přihlášek. Po kliknutí na tlačítko Přihlásit se otevře dialogové okno, které je podobné tomu na stránce s přihlášením na soutěž. V horní části má správce soutěže možnost zvolit přihlášení závodníka z databáze nebo vytvořit jednorázovou přihlášku. Přihlášením závodníka z databáze pomocí jména nebo příjmení se předvyplní údaje a přihláška se spáruje s účtem závodníka. V druhém případě jednorázové přihlášky bez databáze se přihláška nespáruje.<br>
      Na rozdíl od plnohodnotné přihlášky správce soutěže nemusí vyplňovat všechny údaje a má k dispozici seznam všech kategorií nefiltrovaných podle ročníku narození a pohlaví. To znamená, že lze závodníkovi vybrat jakoukoli kategorii a tím nedodržet pravidla ROB. 
      <br>
      <a href="${BaseUrl.value}static/help/event_entry_edit.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_entry_edit.png" alt="" width="1400px">
      </a>
    </p>`
  },


  "event-food-acc": {
    title: "Ubytování a strava",
    content: `
    <p>
     Ve spodním menu může správce soutěže nastavit ubytování a stravu. Tyto položky se nabídnou k objednání v přihlášce. Uložení ubytování a stravy se provede tlačítkem Uložit vpravo dole. 
     <h3>Ubytování</h3>
     Cena za ubytování je na jeden den. ROBis zajistí, aby se nepřekročila kapacita ubytování, pokud je zadána. Pokud pole zůstane nevyplněné, kapacita je neomezená. Datum u ubytování nelze po uložení z technických důvodů měnit. Pokud je potřeba datum změnit, je nutné smazat celé ubytování a vyplnit jej znovu. To nelze, pokud jsou v něm závodníci již přihlášení. Ty je možné odhlásit v editaci přihlášky v sekci "Správa přihlášených". Pozor, na každý den v rozsahu od–do se vytvoří jedna položka ubytování pro přihlášky. Tzn. pokud je soutěž pá–ne 7.–9.6. (přespání pá–so a so–ne), je vhodné uvést datum pouze na dva dny, nejlogičtější bude 7.6.–8.6. a vytvoří se tak dva dny ubytování. Lze uložit maximálně 4 typy ubytování.
     <h3>Strava</h3>
     Jedna tabulka je vždy na jeden den. Počet polí je předem daný, tzn. na snídani, oběd i večeři lze nabídnout až 4 varianty jídel. Pokud nějakou variantu nevyplníte, nebude nabízena. Tabulku je třeba vyplňovat shora dolů, zleva doprava. Přes tlačítko "Přidat další dny jídla" lze přidat další položky pro následující dny. Nezapomeňte vyplnit datum. Pokud je potřeba některý den zrušit, stačí smazat všechna data v polích pro tento den a uložit. 
      <br>
      <a href="${BaseUrl.value}static/help/event_food_acc.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_food_acc.png" alt="" width="1100px">
      </a>
    </p>`
  },


  "event-services": {
    title: "Služby",
    content: `
    <p>
      V nastavení soutěže lze přidat služby, které si závodníci mohou objednat v přihlášce.
    </p>
      <br>
      <a href="${BaseUrl.value}static/help/event_raceservices.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_raceservices.png" alt="" width="900px">
      </a>
      <h3>Půjčení čipu</h3>
    <p>
      Pokud pole neobsahuje cenu (číslo), půjčení čipu nebude vůbec umožněno. Pokud bude hodnota půjčení 0, půjčení čipu je zdarma. Pozor, někteří závodníci nevlastní čip, proto jej v přihlášce nemohou vyplnit. Avšak přihláška vyžaduje mít buď číslo čipu vyplněné, nebo mít zaškrtnuté půjčení čipu. Je vhodné půjčení čipu závodníkům umožnit. Nutnost mít v soutěži čip lze zrušit v nastavení soutěže. Při vytvoření soutěže se automaticky nastaví umožnění půjčení čipu zadarmo.
    </p>

      <h3>Další služby</h3>
    <p>
      Přes tlačítko Přidat službu lze přidat službu, kterou si závodník bude moci vybrat v přihlašování na soutěž. Lze přidat neomezeně variant jedné služby nebo také pouze jednu. Pokud bude mít služba pouze jednu variantu, uživatel bude zaškrtávat checkbox (ano/ne). Je doporučeno přidat popisek, který se zobrazí při zvolení dané varianty v přihlášce, aby uživatelé věděli, o jakou službu se jedná. V přihláškách se vždy zobrazí popisek z první varianty, i když ještě uživatel žádnou variantu nevybral. Lze nastavit také limit pro objednávání, který hlídá, aby více závodníků danou variantu nemohlo objednat, pokud jsou například omezené zásoby. Další služby se započítávají do všech financí.<br>
      Přidání nebo odebrání jednotlivých variant není možné. Lze smazat pouze celou službu se všemi variantami za podmínky, že si službu ještě nikdo neobjednal. Změny v editaci hodnot potvrďte tlačítkem Uložit služby.
    </p>`
  },

  "event-workers": {
    title: "Činovníci a organizátoři soutěže",
    content: `
    <p>
      <a href="${BaseUrl.value}static/help/event_workers.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_workers.png" alt="" width="900px">
      </a>
      <br>
      Stránka soutěže → Nastavení soutěže → Etapa. V etapách lze evidovat organizátory a činovníky, kteří se na organizaci etapy podíleli. V seznamu lidí je uvedena i aktuální třída rozhodčího. Vyplnění je důležité zejména pro <b>započítání průměrů do (nejen) národního žebříčku</b>. Dále se těmto lidem zapíše praxe na jejich profil. Tato praxe je důležitá pro prodloužení tříd rozhodčího nebo při žádosti o vyšší třídu.  
      <br>
      <a href="${BaseUrl.value}static/help/account_experience.png" target="_blank">
        <img src="${BaseUrl.value}static/help/account_experience.png" alt="" width="1300px">
      </a>
      <br>
    </p>`
  },

  "event-apikey": {
    title: "API klíč etapy",
    content: `
    <p>
      API klíč je uveden v nastavení každé etapy. Tento klíč potřebují vyčítací softwary, aby je mohl ROBis při přijímání jejich online výsledků autorizovat. Bez API klíče nelze do ROBisu tato data posílat, aby se zabránilo nechtěnému přepisu dat nebo napadení databáze ROBisu. Více o možnosti posílat do ROBisu data je uvedeno na stránce API v menu.
      Pozor, API klíč nelze resetovat. Proto je třeba s jeho šířením zacházet opatrně. V případě nutnosti jej lze resetovat přes vývojáře ROBisu. Stačí napsat na email uvedený v zápatí.<br> 
      <a href="${BaseUrl.value}static/help/event_apikey.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_apikey.png" alt="" width="900px">
      </a>
    </p>`
  },
  
  "event-startlist-results": {
    title: "Nahrání startovek a výsledků",
    content: `
    <p>
      <b>Kalendář -> soutěž -> nastavení soutěže -> etapa</b>. Po výběru etapy v nastavení soutěže je možné nahrát startovku a výsledky pro etapu. Nahrání výsledků je důležité pro evidenci závodníků, počítání bodů do žebříčků, výkazy pro ČUS a NSA apod. 
      <h3>Startovní listina</h3>  
      ROBis sám neumí startovku vytvořit. Zvolte "Startovka" ze záložky ve spodní části stránky a klikněte na "Nahrát startovku". Vložte soubor CSV, který je vygenerovaný z některých vyčítacích softwarů třetích stran. Klikněte na Nahrát. Pokud se ukáže chybová hláška, je třeba ji vyřešit. V případě potíží je možné kontaktovat nás na emailu dole v zápatí. Pokud se soubor úspěšně nahraje, klikněte na Další. Parametry a formáty jsou uvedeny v tématu <a href="#" data-topic="dev-startlist">Startovka pro vývojáře</a>.  
      <br> 
      <a href="${BaseUrl.value}static/help/event_startlist_1.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_startlist_1.png" alt="" width="900px">
      </a><br>
      <p>
      V dalším kroku se zobrazí kontrola dat. Záznamy mají zabarvené pořadí podle toho, jak se data spárovala s daty v ROBisu:
      </p>
      <li class="bg-green-lighten-2">Zelená - záznam našel shodu s přihláškou závodníka na soutěž, vše je ok</li>
      <li class="bg-orange-lighten-2">Oranžová - záznam nenašel shodu s daty v ROBisu. Závodník se buď nespároval, nebo jeho přihláška na etapu vůbec neexistuje. Přihláška se pro závodníka automaticky vytvoří.</li>
      <li class="bg-deep-orange-lighten-2">Červená - závodník je v ROBisu na etapu přihlášený, ale v nahraném souboru o něm není žádný záznam, nebo se přihláškou nespároval.</li>
      <br>
      <p>
      Záznamy se s daty v ROBisu párují v pořadí: INDEX, Příjmení a Jméno. Po kontrole klikněte na Uložit. ROBis zvaliduje záznamy. Pokud si s chybovou hláškou nevíte rady, podívejte se na konec tohoto tématu, kde jsou vysvětlené. <b>Uložením se přepíše původní startovka</b>.
      </p>
      <br> 
      <a href="${BaseUrl.value}static/help/event_startlist_2.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_startlist_2.png" alt="" width="800px">
      </a>

      <h3>Výsledková listina</h3> 
      <p>
      Parametry a formáty jsou uvedeny v tématu <a href="#" data-topic="dev-results">Výsledky pro vývojáře</a>. Zvolte "výsledky" ze záložky dole a klikněte na "Nahrát výsledky". Typ souboru zvolte podle přípony, kterou soubor má. Soubor získáte vygenerováním z některých vyčítacích softwarů třetích stran. Klikněte na Nahrát. Pokud se ukáže chybová hláška, je třeba ji vyřešit. V případě potíží je možné kontaktovat nás na emailu dole v zápatí. Pokud se soubor úspěšně nahraje, klikněte na Další.
      Ve druhém kroku je možné zkontrolovat, případně doplnit výčet kontrol, které každá kategorie vyhledává. Tento výčet slouží ke zobrazení vyhledávaných kontrol v kategorii a také k určení počtu vyhledávaných kontrol kategorií pro počítání VT (výkonnostního žebříčku). Po kontrole klikněte na Další. 
      </p>
      <a href="${BaseUrl.value}static/help/event_results_1.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_results_1.png" alt="" width="800px">
      </a><br>
      <p>
      V posledním kroku se zobrazí kontrola dat. Záznamy mají zabarvené pořadí podle toho, jak se data spárovala s daty v ROBisu:
      </p>
      <li class="bg-green-lighten-2">Zelená - záznam našel shodu s přihláškou závodníka na soutěž, vše je ok</li>
      <li class="bg-orange-lighten-2">Oranžová - záznam nenašel shodu s daty v ROBisu. Závodník se buď nespároval, nebo jeho přihláška na etapu vůbec neexistuje. Přihláška se pro závodníka automaticky vytvoří.</li>
      <li class="bg-deep-orange-lighten-2">Červená - závodník je v ROBisu na etapu přihlášený, ale v nahraném souboru o něm není žádný záznam, nebo se přihláškou nespároval.</li>
      <p>
        Záznamy se s daty v ROBisu párují v pořadí: INDEX, Příjmení a Jméno. Záznamy je možné upravovat nebo smazat. Po kontrole klikněte na Uložit. ROBis zvaliduje záznamy. Pokud si s chybovou hláškou nevíte rady, podívejte se na konec tohoto tématu, kde jsou vysvětlené. <b>Uložením se přepíšou původní výsledky</b>.
      </p>
      <br>
      <a href="${BaseUrl.value}static/help/event_results_2.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_results_2.png" alt="" width="800px">
      </a><br>


      <h3 style="color:red">Výpis chybových hlášek:</h3> 
      <li><code>Chybný formát relativního času 'xy' pro záznam: xy. Povolený formát je mm nebo mm:ss.</code> - Absolutní čas znamená denní čas, například že závodník startuje v 10:35:00. Relativní čas je v MM:ss od startu 00, např. 85:00. Start 00 se získá z nastavení soutěže. V souboru může být k dispozici pouze jeden z obou časů.</li>
      <li><code>Musí být pro závod v jeho nastavení uložen start 00</code></li>
      <li><code>Chybí umístění nebo čas běhu pro xy, přitom status výsledku je OK.</code> - Status výsledku nemůže být OK, pokud chybí umístění nebo čas běhu.</li>
      <li><code>Kategorie 'xy' nenalezena pro záznam: xy</code> - při nahrávání dat je potřeba mít v etapě v ROBisu vytvořené všechny kategorie, které jsou v souboru odesílány. Tato chyba znamená, že například nebyla nalezena kategorie H pro daného závodníka, protože v etapě není založená kategorie s názvem H. Záznam se nemůže uložit.</li>
      <li><code>Chybí Jméno pro záznam: xy </code> - závodník je ukládán bez křestního jména.</li>
      <li><code>Chybí Příjmení pro záznam: xy</code> - závodník je ukládán bez příjmení.</li>
      <li><code>Chybí Kategorie pro záznam: xy</code> - závodník nemá v záznamu uvedenou kategorii, ve které závodí.</li>
      <li><code>Ukládaná kategorie 'xy' nebyla nalezena</code> - v seznamu kategorií s výčtem kontrol se ukládá kategorie, která v etapě v ROBisu neexistuje. Kategorie musejí mít stejný název, jinak se nespárují, a musejí být v etapě předem vytvořené.</li>
      <li><code>"Kategorie 'xy' nenalezena v hlavní soutěži pro: xy. Musí být vytvořena"</code> - nelze vytvořit přihlášku, protože ukládaná kategorie neexistuje v hlavní soutěži. Je nutné ji v ní nejdříve vytvořit.
      <li><code>Chybí čas běhu nebo umístění pro xy, zatímco status výsledků je OK nebo nevyplněný.</code> - pokud má závodník status výsledku jako OK, pak nelze uložit jeho čas jako 0:00 nebo prázdný, protože takový výsledek by nedával smysl.</li>
      <li><code>"SI číslo: xy", "Startovní číslo: xy", "Index : xy" "má duplicitní údaje"</code> - tento údaj je uveden i jinde v souboru, což není možné, protože tyto údaje musejí být pro každého závodníka unikátní. Např. jsou ukládáni dva závodníci se stejným číslem čipu.</li>
      <li><code>Neplatný formát času běhu: xy pro xy</code> - čas běhu musí být ve formátu MM:ss, např. 98:15.</li>
      <li><code>Neplatný formát mezičasu: xy pro xy</code> - formát mezičasů musí být m:ss nebo MM:ss, např. 5:15.</li>
      <li><code>Neplatná hodnota v umístění: xy pro xy</code> - do proměnné place se neukládá číslo (umístění) ani jeden z možných statusů: OK, MP, DNS, DNF, DSQ, NR, OVT, UNF, ERR. V takovém případě se vypíše chyba.</li>
      <li><code>Neplatný status kontroly: xy pro kód xy u xy</code> - možnosti jsou pouze: OK, MP, DP, AP.</li>
      <li><code>Neplatný typ kontroly: xy pro kód xy u xy</code> - možnosti jsou pouze: CONTROL, FINISH, BEACON, SEPARATOR.</li>
      <li><code>Chybí povinné údaje Příjmení, Jméno nebo název kategorie pro xy SI: xy</code> - jeden z údajů se v souboru neposílá a záznam nemůže být uložen.</li>
      <li><code>V kategorii 'xy' čísla umístění xy obsahují nespojitelnou posloupnost. Chyba na pozici xy</code> - nelze ukládat výsledky s umístěním závodníků, které mají vynechaná čísla. Např. 1, 2, 3, 6, 7 atd. Musí se jednat o postupku. Pokud je nějaké číslo vynechané, zobrazí se chyba. Povoleno je pouze umístění na stejném místě, např. 1, 2, 2, 4, 5.</li>
    </p>
    `
  },

  "commentator-results": {
    title: "Online výsledky pro komentátora",
    content: `
    <p>
      Přístup: Menu -> Výsledky -> Ikonka vedle časovače obnovení dat.<br>
      Stránka obsahuje veškerý servis výsledků, včetně mezičasů z radiokontrol. Řazení výsledků podle kategorií je primárně podle pořadí na posledním mezičase (radiokontroly) závodníka, sekundárně podle času běhu. Závodníci, kteří nemají mezičas se řadí až za tyto závodníky a jsou seřazení podle času sestupně.
    </p>
    <p>
      <b>Zobrazení a ovládání:</b>
      <ul>
        <li>Pokud se výsledek závodníka změnil v posledních 4 minutách, je řádek označený oranžově.</li>
        <li>V mezičasech je hodnota v závorce umístění na tomto mezičase. Nebere se ohled na kódy kontrol. Tzn. nezáleží na pořadí ražení kontrol, bere se pořadí mezičasů. Proto může dojít k nepřesnému seřazování, pokud se některému závodníkovi neodešle oražení mezičasu z radiokontroly.</li>
        <li>Kliknutím na záznam v tabulce POSLEDNÍ VÝSLEDKY se scrolluje na přehled výsledků v kategorii závodníka.</li>
        <li>Kliknutím na záznam závodníka v tabulce záznamů podle kategorie se zobrazí jeho mezičasy.</li>
        <li>Kliknutím na kategorii v postranním panelu s kategoriemi se také zobrazí tabulka dané kategorie.</li>
        <li>Kliknutím na na časovač vlevo dole si uživatel může vybrat interval obnovení výsledků ze serveru.</li>
      </ul>
    </p>
    <br>
    <p>
      Zpět do zobrazení standardních výsledků se dostaneš opětovným kliknutím na přeškrtnutou ikonku komentátora vedle zobrazení časovače vlevo dole.
    </p>
     <br>
      <a href="${BaseUrl.value}static/help/results_speaker.png" target="_blank">
        <img src="${BaseUrl.value}static/help/results_speaker.png" alt="" width="1400px">
      </a>
      `
  },

  "event-protocol": {
    title: "Protokol ze soutěže",
    content: `
    <p>
      V nastavení soutěže lze vyexportovat protokol soutěže, avšak nejdříve v den konání soutěže (předtím je tlačítko schované, ale lze to obejít vyplněním ID soutěže do url pro export DOCX, kterou najdete v menu Exporty). Protokol ze soutěže je povinný výstup pro pořadatele dle pravidel ROB. Protokol má několik povinných částí. Většinu z nich ROBis získá z databáze, pokud data pořadatel v ROBisu vyplní. Je tedy potřeba průběžně doplňovat informace, jako např. činovníci (v jednotlivých etapách), pásmo, limit, výčet vyhledávaných kontrol v kategoriích (buď s importem výsledků nebo přes správu kategorií v jednotlivých etapách), výsledky z etap apod. Např. VT se nemohou vypočítat, pokud nejsou nahrány vyhledávané kontroly v kategorii. 
    </p>
    <p>
      Před uveřejněním protokolu zkontrolujte formátování dokumentu!!! Např. jestli se nezalamuje čas běhu, jméno, jestli je vše vidět, jestli se některé řádky neposunou na další stranu apod. Upravte si dokument vzhledově podle sebe, doplňte logo, změňte barvy apod.
    </p>
     <br>
      <a href="${BaseUrl.value}static/help/event_protocol.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_protocol.png" alt="" width="1400px">
      </a>
      `
  },

  "event-permissions": {
    title: "Pravomoce pro editaci soutěže",
    content: `
    <p>
      Člen oddílu s oprávněním organizátor má právo vytvořit soutěž pod hlavičkou svého oddílu. Po vytvoření mají právo soutěž spravovat:
      <li>Autor, který soutěž vytvořil,</li>
      <li>Organizátoři ze stejného oddílu</li>
      Autor má možnost v nastavení soutěže zvolit správce soutěže. Viz <a href="#" data-topic="event-editors">Správci soutěže</a>. Pokud je toto pole v nastavení soutěže vyplněné, pravomoc organizátor pro soutěž nehraje roli a tytot účty přestanou mít možnost soutěž editovat. V tomto případě může soutěž spravovat:
      <li>Autor, který soutěž vytvořil,</li>
      <li>Správci soutěže uvedení v nastavení soutěže</li>
      Další možnost, jak posílat data o soutěži (startovky, výsledky), je přes API klíč dostupný v nastavení etapy. Viz <a href="#" data-topic="event-apikey">API klíč</a>. Pomocí API klíče se nedá dostat do nastavení soutěže ve webovém rozhraní a měnit údaje o soutěži. Pozor, API klíč nelze resetovat. Proto je třeba s jeho šířením zacházet opatrně. V případě nutnosti jej lze resetovat přes vývojáře ROBisu. Stačí napsat na email uvedený v zápatí. 
    </p>`
  },










  "event-entry": {
    title: "Přihlášení na soutěž",
    content: `
      <a href="${BaseUrl.value}static/help/entry_button.jpg" target="_blank">
        <img src="${BaseUrl.value}static/help/entry_button.jpg" alt="pole pro vyplnění ID účtu" width="800px">
      </a><br>
      <p>
        Po vytvoření přihlášky se uloží termín přihlášky, který se nemění ani po editaci přihlášky, a to i v případě, že už běží jiný termín přihlášek. Pokud si ale uživatel přihlášku smaže, termín se mu smaže také, a pokud jsou otevřené přihlášky už v termínu druhém, závodníkovi se zapíše tento druhý termín, pokud si přihlášku založí znovu. To může mít za následek například zvýšené startovné. 
      </p>

      <h3>Neregistrovaný uživatel</h3>
      <p>Po rozkliknutí soutěže v kalendáři klikněte na přihlásit se a vyplňte údaje. Přihlášku po odeslání nelze editovat, proto si prosím před odesláním přihlášky pečlivě zkontrolujte vyplněné údaje. Změnit údaje může jen organizátor události.</p>
      
      <h3>Registrovaný uživatel</h3>
      <p>Po rozkliknutí soutěže v kalendáři klikněte na přihlásit se. Osobní údaje se automaticky předvyplní podle nastavení v účtu. Přihlášku lze editovat i po odeslání.</p>
      
      <h3>Přihlašování členů rodiny</h3>
      <p>Závodník (i předseda oddílu) si může ve svém nastavení vyplnit správce mých přihlášek, který jej může na soutěže přihlašovat. Oprávněný závodník má poté právo přihlašovat tento účet na soutěže a také upravovat jeho údaje v soutěži do vypršení termínu přihlášek.<br>
        <a href="${BaseUrl.value}static/help/assist_user.png" target="_blank">
          <img src="${BaseUrl.value}static/help/assist_user.png" alt="pole pro vyplnění ID účtu" width="400px">
        </a>
      </p>
    <h3>Přihlášky členů oddílu na soutěž pro správce oddílu</h3>
      <p>Manipulace s přihláškami je pro správce oddílu možná v přihlašování na soutěž. V rozevíracím seznamu se nejdříve řadí přihláška přihlášeného uživatele, poté přihlášky těch účtů, které má přihlášený uživatel oprávněné upravovat, a nakonec se zobrazí seznam členů oddílu vzestupně podle příjmení.</p>
    <h3>Na co je "hlavní kategorie"?</h3>
    <p>V případě přihlášek např. na soustředění nemusí být vypsána žádná etapa. V tomto případě by něšlo vybrat kategorii, ve které chce být závodník zapsaný, popř. by nešly vybrat poplatky spojené s toutto kategorií. Proto byl zaveden výběr "hlavní kategorie" aby byl přihlášený závodník vždy v nějaké kategorii veden. Dále se pomocí této kategorie filtrují závodníci na stránce soutěže v sekci "přihlášení závodníci". </p>

    <h3>Ubytování a strava</h3>
      <p>Pokud to pořadatel umožňuje, lze společně s přihláškou na závod přihlásit ubytování nebo stravu. V případě ubytování se automaticky vyplňuje typ ubytování, který uživatel v některém z polí vybere. Předejde se tím například změně ubytování v polovině trvání soutěže a zmatkům s ubytováním. Automaticky se vyberou všechny dny ubytování, ale lze je jednotlivě zrušit.</p>

    `
  },

  "event-bulk-entry": {
    title: "Hromadné přihlašování",
    content: `
    <p>
      <a href="${BaseUrl.value}static/help/event_bulk_entry_1.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_bulk_entry_1.png" alt="" width="600px">
      </a>
      <br>
      Na stránce přihlašování na soutěž, kde se vytvářejí nové přihlášky, je zelené tlačítko "Hromadná přihláška". Toto tlačítko je dostupné pouze tehdy, když je v seznamu pro vytvoření přihlášky možné přihlásit více než jednoho člověka. Kliknutím na tlačítko se otevře dialogové okno. Každý řádek patří jednomu závodníkovi. Kromě cen tam jsou všechny možnosti přihlášky jako na stránce pro jednotlivé přihlašování. <br>
      Druh objednávané stravy poznáte tak, že najedete myší na checkbox, kde se zobrazí, zda je dané jídlo snídaně, oběd nebo večeře. V režimu select boxu je to uvedeno v názvu pole. Pozor, dialogové okno má stránkování kvůli optimalizaci načítání. Na další stránku se přejde dole uprostřed. Stránkování je po 20 lidech. V horní části okna je možnost vyhledávání podle jména závodníka. Filtr se aktivuje po zadání nejméně 3 písmen. Je vhodné psát s diakritikou.<br>
      Odesílají se závodníci, kteří mají <b>zaškrtnutý checkbox</b> vlevo od jména. Pokud není zaškrtnut nikdo, žádná data se neodesílají. Po úspěšném přihlášení lze závodníky upravovat pouze jednotlivě na stránce přihlášení. 
      <br>
      <a href="${BaseUrl.value}static/help/event_bulk_entry_2.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_bulk_entry_2.png" alt="" width="1200px">
      </a><br>
            <a href="${BaseUrl.value}static/help/event_bulk_entry_3.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_bulk_entry_3.png" alt="" width="800px">
      </a>

      <br>
    </p>`
  },

  "event-organizers-entry": {
    title: "Přihlašování organizátorů",
    content: `
    <p>
      Od verze 0.4.5 má správce soutěže možnost vytvořit kategorii ORG v nastavení soutěže. Tato kategorie se zobrazuje všem ze stejného oddílu, jako je pořádající oddíl. 
      <br>
      <a href="${BaseUrl.value}static/help/event_organizers_entry.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_organizers_entry.png" alt="" width="120px">
      </a>
      <br>
      Myšlenkou je začlenit organizátory do přihlášek kvůli přihlašování ubytování a stravy na závodech nebo jen kvůli přehledu. Kategorie ORG se sama automaticky s vytvořením etapy nezaloží. Jak spravovat kategorie najdete v tématu <a href="#" data-topic="event-category">Správa kategorií</a>.
    </p>`
  },



  "event-automodule": {
    title: "Automodul",
    content: `
        <p>Automodul slouží k rozdělování lidí do dopravy. Minimální počet míst je 1, maximální počet míst je 50. Dobrá praxe pro nejsnadnější kooperaci je taková, že všichni lidé s možností vzít auto jej založí. Zbytek se do aut přihlásí. Pokud je aut nedostatek nebo přebytek, je potřeba, aby se účastníci domluvili a počet vyvážili. Automodul mohou správci soutěže pro soutěž vypnout, a to v "dalším nastavení soutěže".</p>
        <h3>Rozvržení automodulu:</h3>
        <p>První pole je pole řidiče. Na něj se vypíše vyúčtování cestovného. Editovat dopravní prostředek a řidiče může pouze řidič sám, autor auta a správce oddílu. Pokud snížíte počet míst v automodulu, smažou se i přihlášení cestujících a to odzadu. Nelze smazat místa při přítomnosti jednosměrných cest. Příjmení a Jméno vkládejte v tomto pořadí a ve správném formátu ať se data správně spárují. Jméno lze vybrat z nabídky. Po rozkliknutí tlačítka Zobrazit všechnu dopravu se nabídnou všichni přihlášení, ne jen ti z oddílu. Pokud oddíl využívá vyúčtování cestovného, doplňte potřebné údaje v editaci automodulu a v nastavení uživatele (jen řidič). Cestujícímu lze přiřadit cestu pouze jedním směrem! Stačí v režimu editace sedadla kliknout na šipečky označující obousměrnou cestu a ta se změní pouze na jeden nebo druhý směr. Toto se projeví i v cesťáku.<br>
        Stažení cesťáku je možné v režimu editace celého auta. v této editaci se také vyplňují všechny potřebné informace z cesty pro doplňění do cesťáku.
        <br>
        <a href="${BaseUrl.value}static/help/event_automodule.png" target="_blank">
          <img src="${BaseUrl.value}static/help/event_automodule.png" alt="" width="1100px">
        </a>
        <h3>Pravomoce:</h3>
        <li>Autor a řidič auta může editovat všechny místa ve svém autě a auto samotné.</li>
        <li>Správce oddílu může editovat všechny auta, které jsou z jeho oddílu.</li>
        <li>Konkrétní místo v autě může editovat člověk, který na místě sedí, autor, který provedl zápis, řidič a autor, který vytvořil dané auto.</li>
      
          </p>
`
  },

  "event-livelox": {
    title: "Livelox",
    content: `
        <h3>Závodník</h3>
        <p>Zaregistrujte se na <a href="https://www.livelox.com/" target="_blank">livelox.com</a> a připojte si ve svém účtu aplikaci, ze které se budou do Liveloxu nahrávat data z hodinek. Při propojování se službou zaškrtněte všechna políčka. Vyberte pouze jednu aplikaci pro nahrávání, aby se záznamy neduplikovaly. Nastavte v účtu soukromí na veřejné.<br>
        Postupy se budou k soutěžím nahrávat automaticky po vyčtení hodinek.</p>
        <h3>Pořadatel</h3>
        <p>Zaregistrujte se na <a href="https://www.livelox.com/" target="_blank">livelox.com</a>. V menu klikněte na Spravovat a poté na Přidat akci. Vyplňte pole podle potřeby. Věnujte zvýšenou pozornost sekcím <b>Čas zveřejnění a Správci</b>, aby se mapa k závodníkům nedostala dříve, než odjedou na start. <b>Vyhledatelnost ponechte odškrtnutou</b>, aby se akce nezobrazovala v kalendáři akcí na Liveloxu. Ve správě události zvolte Upravit mapu. Mapu lze vložit nahráním OCAD, KMZ nebo obrázkového souboru. Doporučuje se vložit mapu již se zakreslenými kontrolami. Lze vložit i prázdnou mapu a tratě importovat z OCADu ve správě události, ale je to složitější a má to mouchy. V dalším kroku, pokud je mapa georeferencovaná, je třeba zkontrolovat, zda sedí, a případně ji upravit. Pokud se vkládá obrázek, je nutné mapu georeferencovat, aby seděla se skutečností. Doporučuje se vytvořit alespoň 3 body. Uložit.
        </p>
        <p>Nyní je potřeba importovat účastníky, aby Livelox dokázal spárovat uživatele s kategorií, jinak bude nutné uživatele přidávat ručně. V nastavení soutěže je možné exportovat jakýkoli XML soubor (přihlášení závodníci, startovka i výsledky). Nejlepší je před závodem nahrát do Liveloxu seznam závodníků a po skončení závodu nahrát výsledky. Po stažení souboru jej nahrajte ve správě soutěže v Liveloxu přes <b>import výsledků</b> v okně "Startovní časy a výsledky". Po nahrání do Liveloxu se otevře okno s kategoriemi. Pokud ještě nebyly vytvořeny, zvolte u každé kategorie druhou možnost - <b>create new class</b>. Poté proveďte import. V nastavení kategorií ještě propojte kategorie s tratěmi. Hotovo, uživatelé se po vyčtení hodinek obvykle sami nahrají do postupů.</p>
        <p>Nezapomeňte vložit URL adresu na stránku soutěže v ROBisu, aby se na ni uživatelé dostali. URL může vypadat například takto: https://www.livelox.com/Events/Show/117784/MCR35MHz.</p>
    `
  },

  "event-payments": {
    title: "Přehled plateb",
    content: `
        <p>
          Přehled plateb je k dispozici v každé soutěži, pokud už je do soutěže někdo přihlášený. Karta zobrazuje přehled služeb, které mají závodníci objednané, včetně jejich varianty. Poslední sloupec zobrazuje částku, kterou je nutné pořadateli zaplatit. Ceny zahrnují veškeré přihlášené služby viditelné v tabulce + <b>startovné</b>. Na začátku karty je přes tlačítko "Stáhnout tabulku" možné stáhnout všechna data do Excelu.
        </p>
          <a href="${BaseUrl.value}static/help/event_payments.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_payments.png" alt="" width="1200px">
      </a><br>
      
    
    `
  },

    "event-absences": {
    title: "Omluvenky",
    content: `
        <p>
            Omluvenky se <b>na stránce soutěže</b> zobrazují jen pro akce reprezentací. Pro omluvení své absence musí být závodník přihlášený a musí být součástí reprezentace, která akci pořádá. Realizační tým se omlouvat nemůže.<br>
            Závodník může omlouvat i účty, ke kterým má přístup v rámci <a href="#" data-topic="event-entry">Správce přihlášek</a>, viz přihlašování členů rodiny.<br>
            Pokud závodník není v soutěži přihlášen, na stránce soutěže pod tlačítkem PŘIHLÁSIT SE se zobrazí také tlačítko OMLUVENKA. Pokud je závodník na soutěž přihlášený, tlačítko Omluvenka se mu nezobrazí a v seznamu omluvených také nefiguruje.<br>
            Kliknutím na tlačítko Omluvenka se zobrazí dialogové okno, kde lze přes tlačítko + nebo koš omluvenku vytvořit nebo smazat. 
        </p>
          <a href="${BaseUrl.value}static/help/event_absences_create.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_absences_create.png" alt="" width="600px">
      </a><br>
        <p>
          Přístup k seznamu omluvených mají jen správci reprezentace, která akci pořádá přes Soutěž -> Přihlášení závodníci -> Omluvenky.<br>
        </p>
          <a href="${BaseUrl.value}static/help/event_absences_list.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_absences_list.png" alt="" width="800px">
      </a><br>
    `
  },

  "event-close": {
    title: "Uzavření soutěže",
    content: `
        <p>
          Soutěž může uzavřít správce soutěže jako prevenci před případnými nechtěnými změnami v soutěži prostřednictvím tlačítka "Uzavřít soutěž" v nastavení soutěže. Většina ikon a tlačítek poté zešedne a nebude možné je použít ke změně některých údajů soutěže. Povolená je pouze správa <b>souborů a odkazů</b>.
        </p>
          <a href="${BaseUrl.value}static/help/event_close_buttons.png" target="_blank">
        <img src="${BaseUrl.value}static/help/event_close_buttons.png" alt="" width="900px">
      </a><br>
        <p>
          Pokud uzavření neprovede nikdo ze správců, <b>soutěž se automaticky uzavře 30 dnů po skončení soutěže</b>. Soutěže k uzavření se kontrolují automaticky každý den po půlnoci.<br>
          Uzavření (uzamčení) je z důvodu zabezpečení, aby někdo např. nezměnil výsledky, přihlášené závodníky apod. dávno po proběhnutí akce.<br>
          Společně s uzavřením se ještě jednou přepočítá VT a body.<br>
          Pokud potřebujete soutěž z nějakého důvodu obnovit, kontaktujte uživatele s pravomocí manažer (asociační) nebo správce ROBisu přes email uvedený v zápatí. 
        </p>
    `
  },



  "dev-info": {
    title: "Obecné informace",
    content: `
        <h3>Obecné informace</h3>
        <p>ROBis využívá CSV, XML a JSON. Formát a adresy jsou k dispozici v položce menu - Exporty. <br> XML strukturu vymyslela IOF a <a href="https://github.com/international-orienteering-federation/datastandard-v3" target="_blank">celá dokumentace</a> je k dispozici na <a href="https://orienteering.sport/iof/it/data-standard-3-0/" target="_blank">tomto rozcestí</a>. Kvůli odlišnostem ROBu bylo nutné přidat rozšíření, jehož dokumentace je na <a href="https://github.com/AROB-CR/radio-o-standards/tree/main/ardf-xml" target="_blank">GitHubu</a>.<br> JSON formát posílá stejná data jako XML, ale jeho syntaxe není tak složitá, takže celkový počet přenášených dat je nižší. Struktura JSON formátu i s příklady pro ARDF je k <a href="https://github.com/AROB-CR/radio-o-standards/tree/main/ardf-json" target="_blank">nahlédnutí zde</a>.</p>
      
    `
  },
  "dev-sidroid": {
    title: "SI Droid Event",
    content: `
      <h3>SI Droid Event</h3>
        <p>Export záznamů do CSV pro <a href="https://play.google.com/store/apps/details?id=se.joja.sidroid.event.full" target="_blank">SI Droid Event</a> je možný jako souhrn všech účtů v ROBisu (na stránce export) nebo v sekci startovek v nastavení závodu v rozevíracím seznamu export, pouze pro konkrétní závod. Je zde přidána i kategorie, ve které závodník v daném závodě běží. Protože číslo čipu a jméno jsou pro SI Droid povinné, záznamy s těmito chybějícími údaji se neexportují.</p>

      
    `
  },
  "event-xls": {
    title: "Seznam závodníků",
    content: `
     <h3>Seznam závodníků (pouze export)</h3>
        <ul>
          XLSX - slouží pro oddíly a pořadatele pro souhrn informací přihlášených závodníků<br>
          Poznámky:
          <li>Autoriz. = autorizovaný závodník. Je to závodník, který se přihlásil na závod pomocí svého účtu v ROBisu a jeho údaje jsou tedy ověřené, například jméno, příjmení, index apod.</li>
          <li>Startovní skupina - Pokud pořadatel v nastavení soutěže zvolí možnost přihlášek do startovních skupin, pak se v přehledu závodů zobrazí takto: 0 - první startující, 1 - první skupina, 2 - druhá skupina, 3 - třetí skupina, 4 - poslední startující.</li>
        </ul>      
    `
  },

    "dev-entries": {
    title: "Seznamy přihlášených (export)",
    content: `
        <p>
            Soutěž → Nastavení → Správa přihlášených. Kódování <b>UTF-8</b>, není-li uvedeno jinak.<br>
          Formáty pro import a jejich struktura:
        </p>       
                    
        <ul>
          <li><b>XLSX</b></li>
            Slouží pro oddíly a pořadatele pro souhrn informací přihlášených závodníků<br>
            Autoriz. = autorizovaný závodník. Je to závodník, který se přihlásil na závod pomocí svého účtu v ROBisu a jeho údaje jsou tedy ověřené, například jméno, příjmení, index apod.<br>
            Startovní skupina - Pokud pořadatel v nastavení soutěže zvolí možnost přihlášek do startovních skupin, pak se v přehledu závodů zobrazí takto: 0 - první startující, 1 - první skupina, 2 - druhá skupina, 3 - třetí skupina, 4 - poslední startující.
        </ul>
        <br>
        <ul>
          <li><b>ARDF JSON:</b></li>
          Schéma vychází z <a href="https://github.com/AROB-CR/radio-o-standards/blob/main/ardf-json/ardf_schema.json" target="_blank">ARDF JSON standardu</a>.<br>
          Příklad datového souboru je <a href="${BaseUrl.value}static/GET_race.json" target="_blank">tady</a>.<br>
          Export zahrnuje celou etapu, takže v případě, že jsou v etapě nahrané výsledky, zahrne do exportu i je.
        </ul>
        <br>
        <ul>
          <li><b>Matula CSV:</b></li>
          Kódování <b>windows-1250</b>.<br>
          Formát: Příjmení a Jméno;Index;Číslo čipu;Rok narození;Pohlaví;Země;Kategorie<br>
          Pohlaví: M - Muž, F - Žena<br>
          Země: 3 písmený kód země podle ISO 3166-1 alpha-3, např. CZE<br>
          Příklad: Novák;Petr;GBM8105;1981;M;CZE;M20<br>
        </ul>
        <br>
        <ul>
          <li><b>XML:</b></li>
          Schéma vychází z <a href="https://github.com/international-orienteering-federation/datastandard-v3" target="_blank">IOF 3.0</a> s <a href="https://github.com/AROB-CR/radio-o-standards/tree/main/ardf-xml" target="_blank">ARDF rozšířením</a>.<br>
          Pokud závodník potřebuje půjčit čip, element ControlCard není uvedený.
        </ul>
        <br>
        <ul>
          <li><b>ARDF manager CSV:</b></li>
          Formát: Číslo čipu;Start. číslo;Jméno;Příjmení;Kategorie;Pohlaví;Rok narození;Oddíl;Index;Relativní Start;Půjčení čipu<br>
          Pohlaví: 1 - Muž, 0 - Žena<br>
          Půjčení čipu: 1 - Ano, 0 - Ne<br>
          Relativní start: čas od startu 00, formát MM:ss (např. 85:00)<br>
          Příklad: 2045258;1;Petr;Novák;M20;1;1981;SK Radiosport;GBM8105;65:30;0<br>
        </ul>
        <br>
        <ul>
          <li><b>OCM CSV:</b></li>
          Formát: Číslo čipu;Jméno;Příjmení;Kategorie;Pohlaví;Rok narození;Volačka;Země;Start. skupina;Start. číslo;Index<br>
          Pohlaví: 1 - Muž, 0 - Žena<br>
          Volačku ROBis nepodporuje, proto je toto místo prázdné.<br>
          Země: 3 písmený kód země podle ISO 3166-1 alpha-3, např. CZE<br>
          Startovní skupina: 0 - první startující, 1 - první skupina, 2 - druhá skupina, 3- třetí skupina, 4- poslední startující.<br>
          Příklad: 2045258;1;Petr;Novák;M20;1;1981;;CZE;2;56;GBM8105<br>
        </ul>
        <br>
        <ul>
          <li><b>FjwW CSV:</b></li>
          Formát: Startovní číslo;Oddíl;Kategorie;Příjmení;Jméno;Volačka(index)číslo čipu;Startovní skupina;Čas startu<br>
          Startovní skupina: 0 - první startující, 1 - první skupina, 2 - druhá skupina, 3- třetí skupina, 4- poslední startující.<br>
          Čas startu má formát hh:mm:ss<br>
          Příklad: 2045258;SK Radiosport;M20;Novák;Petr;;2;65:30<br>
        </ul>

    `
  },



  "dev-startlist": {
    title: "Startovka (import)",
    content: `
        <p>
          ROBis podporuje hned několik typů importů a exportů startovních listin v závislosti na typu souboru a zdrojovém softwaru. Kódování <b>UTF-8</b>, není-li uvedeno jinak.<br>
          Formáty pro import a jejich struktura:
        </p>       
                    
        <ul>
          <li><b>CSV:</b><br>
          Pokud potřebujete v CSV formátu některý údaj vynechat, nevypisujte mezi ; a ; vůbec nic. Např. 3;Novák;Petr;M20;;11:05:30;GBM8105;OK123;;2045258
          Absolutní čas startu je podporován jako <code>hh:mm:ss</code>. Datum startu se do databáze uloží z datumu konání závodu. Mohou být vyplněné oba druhy startů, nebo pouze jeden.
          Uložení volačky ROBis nepodporuje
          Stát - podporujeme pouze formát ISO 3166-1 alpha-3, což znamená třípísmenný kód zemí. Např. CZE.</li>
          <br>
          <code>Startovní číslo;Příjmení;Jméno;kategorie;relativní čas startu;absolutní čas startu;index;volačka;stát;číslo čipu</code><br>
          Např. <code>3;Novák;Petr;M20;65:30;11:05:30;GBM8105;OK123;CZE;2045258</code>
            
        </ul>
        <br>
        <ul>
          <li><b>FjwW JSON:</b><br>
            Formát s příponou JSON, který používá stejnojmený německý program pro vyčítání. 
            Příklad datového souboru je <a href="${BaseUrl.value}static/startlist_FjwW.json" target="_blank">tady</a>.<br>
            Vytvořit jej můžete zde:
            <br>
            <a href="${BaseUrl.value}static/help/event_fjww_json.png" target="_blank">
              <img src="${BaseUrl.value}static/help/event_fjww_json.png" alt="" width="700px">
            </a>
          </li>
        </ul>


          <!--
          Vytvořeno pomocí této <a href="https://github.com/international-orienteering-federation/datastandard-v3/blob/master/examples/StartList1.xml" target="_blank">dokumentace</a>.<br>
          Ve vzorovém souboru jsou uvedeny všechny hodnoty, které ROBis v seznamu generuje - <a href="https://github.com/AROB-CR/radio-o-standards/blob/main/ardf-xml/startlist_individual.xml" target="_blank">Vzorová startovka.xml</a>
          <li>Pozor, kategorie je nadřazený element pro všechny přihlášené v dané kategorii. Tzn. kategorie závodníka není uvedena u jeho výpisu dat, ale je uvedená pouze jako element, který obsahuje závodníky, kteří do této kategorie spadají.</li><br>
          Povinné údaje pro import startovky do ROBisu (dodržte strukturu xml souboru viz vzor):
          <li><code>&lt;ID type="ROBIS"&gt;*ID*&lt;/ID&gt;</code> (ID soutěže je například v URL adrese) (Nutné vyplnit pouze, pokud se startovka nenahrává přes web)</li>
          <li><code>&lt;Time&gt;xx:xx:xx&lt;/Time&gt;</code> (Start 00, pokud se liší od startu v ROBisu)</li>
          <li><code>&lt;ID type="INDEX"&gt;GBM5030&lt;/ID&gt;</code> (Index závodníka)</li>
          <li><code>&lt;StartTime&gt;xx:xx:xx&lt;/StartTime&gt;</code> (Start závodníka)</li>
            -->

    `
  },
  "dev-results": {
    title: "Výsledky (import)",
    content: `
        <p>
          ROBis podporuje hned několik typů importů v závislosti na typu souboru a zdrojovém softwaru. Kódování UTF-8, není-li uvedeno jinak.<br>
          Formáty pro import a jejich struktura:
        </p>       
                          
        <ul>
            <li><b>CSV:</b><br>
              Jedná se o vylepšený OCM CSV soubor. Formát nepočítá s týmovým hodnocením ani štafetami. Naopak počítá s daty, která specifikují některé věci a ta se poté mohou lépe a přesněji zobrazit ve výsledkovém servisu. CSV soubor rozlišuje tři různé řádky: <b>Hlavičku závodu, Hlavičku kategorie, Výsledek závodníka</b>. Na pořadí řádků nezáleží. Údaje, které ROBis nezpracovává, jsou označené <b>*</b>. Pokud se neodesílají, pošle se v CSV prázdná hodnota: ;;<br>
              <code>*RH (race header); Race name; Start datetime 00; Race type; Race level; Race band; Race time limit </code><br>
              <code>CH (category header); Category name*; Category race type*; Limit*; Band*; Length*; Climb*; Control point string (např. 1 F2 3 M)</code><br>
              <code>R (result header); Category name; Place; Result status; Last name; First name; Start number; Index; Club name*; Run time; Number of control points found; Punches (viz níže)</code><br>
              <code>Alias (např. F1, nebo kód), Split time (MM:ss), Punch status, Control status</code>. Ražení je odděleno #<br>
              Příklad řádků:<br>
              <code>RH;MČR 144 MHz;2024-12-01T08:30:00;CLASSIC;NATIONAL;M2;240<br>
                CH;M20;ORIENTEERING;120;M2;10.5;500;1 2 3 4 5 6 7 M<br>
                R;M20;1;OK;Novák;Petr;101;CZE123;GBM;52:27;9;1,05:30,OK#2,5:10,OK#3,8:12,OK#4,6:15,OK#5,4:40,OK#6,2:50,OK,SEPARATOR#7,7:10,OK#M,12:20,OK,BEACON#F,0:20,OK,FINISH<br>
              </code><br>
              Hodnota v proměnných:
              <table class="result_csv_table">
                <thead>
                  <tr>
                    <th>Race type*</th>
                    <th>Race level*</th>
                    <th>Race band*</th>
                    <th>Result status</th>
                    <th>Punch status</th>
                    <th>Control type</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      CLASSIC<br>SPRINT<br>FOXORING<br>LONG
                    </td>
                    <td>
                      INTERNATIONAL<br>NATIONAL - I. stupeň<br>REGIONAL - II. stupeň<br>DISTRICT - III. stupeň<br>PRACTICE<br>OTHER
                    </td>
                    <td>
                      M2<br>M80<br>COMBINED
                    </td>
                    <td>
                      OK - valid<br>MP - chyba ražení<br>DNS - nestartoval<br>DNF - nedokončil<br>DSQ - diskvalifikován<br>
                      NR - nehodnocen (např. nenašel/a mimo maják jinou kontrolu)<br>OVT - překročil limit<br>UNF - Neoficiální
                    </td>
                    <td>
                      OK<br>MP - chyba ražení (maják není poslední kontrolou)<br>DP - duplikované ražení<br>
                      AD - kontrola navíc
                    </td>
                    <td>
                      CONTROL<br>FINISH<br>BEACON<br>SEPARATOR
                    </td>
                  </tr>
                </tbody>
              </table> 
              Detailnější informace na <a href="https://github.com/kolskypavel/ARDF-Manager_mobile/wiki/Data-formats#4-results-export" target="_blank">githubu</a>.           
            </li>
            <li><b>CSV OCM:</b><br> 
              ROBis zpracovává 2 řádky, které se odlišují druhým parametrem. Nabývá hodnot H nebo "" (prázdná hodnota). H - definice vyhledávaných kontrol pro kategorii. "" - výsledek závodníka. Výpis kontrol nebo mezičasů je oddělen čárkou ",". Odlišení jednotlivých kontrol nebo mezičasů je pomocí "#". Typ (kontroly) nabývá hodnot 0 (standardní), 1 (maják), 2 (divácká). Status (kontroly) nabývá hodnot 0 (OK), 1 (špatná), 2 (neznámá). Více v <a href="${BaseUrl.value}static/OCM_dokumentace_vysledky.jpg" target="_blank">dokumentaci OCM</a>. Přijímaná struktura definice kontrol a výsledku závodníka:<br>
              <code>Název kategorie;H;Limit;Pásmo;Relativní start;Kontroly: Typ,Kód,Alias#</code><br>
              <code>Název kategorie;;Umístění;Startovní číslo;Příjmení;Jméno;Čas běhu;Počet kontrol;Mezičasy: Kód,Mezičas,Alias,Typ,Status#;Země;Číslo čipu;Zkratka oddílu;Index</code>
            </li>
            <li><b>XML IOF 3.0 + ARDF</b><br>
              Používáme standardní formát podle IOF 3.0 + rozšíření ARDF. IOF nepočítá s některými daty, která při importu využíváme. Např.: rozlišení typu kontroly: <code>Beacon</code> (maják), <code>Separator</code> (divácká), <code>Control</code>. Počet kontrol se počítá na základě mezičasů, které nemají Missing nebo Additional. Pro ARDF účely však byl do Extensions výsledku přidán počet nalezených kontrol. Pro účely hromadného nahrávání výsledků jsme do extensions přidali souhrn vyhledávaných kontrol v kategorii. Kódování je podporováno jak UTF-8, tak windows-1250. Kódování je nutné uvést na prvním řádku, např. <code>&lt;?xml version="1.0" encoding="utf-8"?&gt;</code>. <a href="https://github.com/AROB-CR/radio-o-standards/blob/main/ardf-xml/results_individual.xml" target="_blank">Vzorový soubor výsledků.xml</a>.
            </li>

            <li><b>ARDF JSON:</b><br>
              Formát s příponou JSON, jehož schéma bylo vytvořeno přímo pro ROB - ARDF JSON standard. Příklad datového souboru naleznete <a href="${BaseUrl.value}static/POST_results_example.json" target="_blank">tady</a>.<br>
            </li>
                    
            <li><b>FjwW JSON:</b><br>
              Formát s příponou JSON, který používá stejnojmenný německý program pro vyčítání. 
              Příklad datového souboru je <a href="${BaseUrl.value}static/results_FjwW.json" target="_blank">tady</a>.<br>
              Vytvořit jej můžete zde:
              <br>
              <a href="${BaseUrl.value}static/help/event_fjww_json.png" target="_blank">
                <img src="${BaseUrl.value}static/help/event_fjww_json.png" alt="" width="700px">
              </a>
            </li>

        </ul> 

    `
  },

  "dev-online-results": {
    title: "Online výsledky",
    content: `
              
        <p>
          ROBIS umožňuje nahrávání online výsledků softwarům třetích stran přes API (<a href="https://cs.wikipedia.org/wiki/API" target="_blank">co je API - Wikipedie</a>). Na adresu API (endpointu) software pošle příslušná data a Race-Api-Key, kterým se autorizuje. Tento klíč je uveden v nastavení každé etapy, viz téma <a href="#" data-topic="event-apikey">API klíč etapy</a>. Online výsledky lze na endpoint poslat buď jako soubor CSV nebo JSON. JSON je preferovaný, protože ho ROBis zpracuje rychleji. Více o API je uvedeno v <a href="#" data-topic="dev-api">tématu API</a>.
        </p>       
                          
      `
  },


  "dev-api": {
    title: " ",
    content: 
              
           
          ""                
      
  },


  
  "club-members": {
    title: "Správa členů",
    content: `
        <p>Pokud je uživatel veden jako předseda svého oddílu, může měnit pravomoce (organizátor a správce oddílu) a spravovat členy v oddíle. Správa členů se děje prostřednictvím tlačítek v posledním sloupci tabulky Správa:</p> <a href="${BaseUrl.value}static/help/predseda_edit.png" target="_blank"><img src="${BaseUrl.value}static/help/predseda_edit.png" alt="ikony akce pro editaci členů oddílu" width="100px"></a>
        <p>Přes jednotlivá tlačítka předseda může:</p>
          <li><b>měnit údaje členům oddílu</b> (jméno, příjmení, email, index, číslo SI, rok narození, pohlaví),</li> 
          <li><b>vyloučit uživatele z oddílu</b> (odebrat jim index a tím příslušnost k oddílu),</li> 
          <li><b>smazat účty</b>, které jsou mladší než <b>14 dní</b>.</li>
          <br>
          <p>
          Přes editaci účtu může předseda nastavit (ale i sám uživatel v sekci nastavení) účet, který bude moci daného uživatele přes sebe v ROBisu na soutěže přihlašovat. Takto mohou rodiče přes svůj účet přihlašovat své děti.<br>
          <a href="${BaseUrl.value}static/help/club_person_assist_user_edit.png" target="_blank"><img src="${BaseUrl.value}static/help/club_person_assist_user_edit.png" alt="přidání uživatle pro povolení editace jiného účtu" width="600px"></a><br>
          Zrušení členství uživatele způsobí to, že uživateli bude ukončena role v oddílu a přiřadí se mu role nová, která nemá oddíl. Uživatel bude moci dál spravovat svůj účet a přihlašovat se na závody, ale bez členství v oddílu. V případě, že se chce uživatel připojit do oddílu, musí tuto akci provést předseda daného oddílu výběrem závodníků bez oddílu ze seznamu pod tlačítkem registrace. (bude doděláno)
        </p>
                
    `
  },

  "club-payment-rules": {
    title: "Pravidla financování",
    content: `
        <p>
          Sekci financování vidí jen členové oddílu. Uživatel, který je součástí oddílu a má pravomoc správce oddílu, může přes tužku měnit pravidla a vytvářet nová.<br>
          <h3>Úprava pravidel</h3>
          Pravidla upravujte, pokud se při jejich vytváření udělala chyba nebo pokud se situace změnila tak, že už není vhodné evidovat předchozí období rozdílným způsobem. Úpravou se totiž změní všechny částky v minulosti, které se počítají na základě pravidla (podle data). Pokud se například změní amortizace na základě rozhodnutí rady, je třeba vytvořit nové pravidlo, které bude mít jinou amortizaci, a staré ponechat. Jinak se bude nová amortizace počítat i na cesťáky zpětně, což není žádoucí. Popisek je pouze informační.
          <h3>Příspěvek oddílu</h3>
          Oddíl může přispívat svým členům např. na dopravu, startovné apod. V procentech lze vyjádřit kolik oddíl proplácí. Tento odečet se projeví POUZE na stránce Moje finance.  
          <h3>Vytvoření nového pravidla</h3>
          Vytvořením nového pravidla se automaticky ukončí platnost toho starého. Staré peněžní částky tak zůstanou beze změny a budou se řídit podle starých pravidel. Vytvoření nového pravidla je vhodné například při schválení nové amortizace, kdy je žádoucí, aby se počítala pouze na určité období a ne zpětně.  
        </p> 
        <a href="${BaseUrl.value}static/help/club_payment_rules.png" target="_blank"><img src="${BaseUrl.value}static/help/club_payment_rules.png" alt="ikony akce pro editaci členů oddílu" width="1000px"></a>
        <p>
          
        </p>
                
    `
  },


  "club-payments": {
    title: "Účetnictví",
    content: `
        <p>
          Účetnictví mohou zobrazit správci oddílu. Nacházejí se zde stejné informace, jako jsou v každé soutěži v záložce "Přehled plateb". Účetnictví se týká pouze členů oddílu. Pro vyúčtování všech soutěžících v soutěži (pokud ji například oddíl pořádá) je potřeba přejít do přehledu plateb na stránkách soutěže, nebo si stáhnout excelovskou tabulku, kde jsou závodníci i z jiných oddílů. Účetnictví je filtrované podle kalendářního roku, který lze změnit vlevo dole. 
        </p> 
          
        <a href="${BaseUrl.value}static/help/club_payments.png" target="_blank"><img src="${BaseUrl.value}static/help/club_payments.png" alt="ikony akce pro editaci členů oddílu" width="1550px"></a>        
    `
  },


  "club-automodules": {
    title: "Správa automodulů",
    content: `
        <p class="mb-4">
          Záložku mohou zobrazovat jen správci oddílu. Obsahuje seznam všech automodulů v soutěžích za kalendářní rok. Rok lze měnit vlevo dole. Sloupec Částka zobrazuje hodnotu, na kterou je vypsaný cesťák. Částku nezahrnují osoby v autě, které nenáleží oddílu, pro který bylo auto vypsáno. Také se tam nezobrazí osoby, které jsou sice z daného oddílu, ale za určitých okolností se správně nespárují. Např. pokud nejsou na soutěž přihlášení nebo se do auta přihlásili dříve, než si vytvořili v soutěži přihlášku.<br>
          Po uzavření soutěže se sice zabrání změně údajů např. pro startovné, ubytování apod., ale nezabrání se možnosti změnit údaje v autech (počet kilometrů, pasažéři apod.). Automodul si každý oddíl spravuje sám. Pokud oddíl dopravu využívá a je potřeba ji uzavřít jako vyúčtovanou, je nutné zatrhnout zaškrtávací pole vpravo. Zaškrtnutím se auto uzavře a nelze jej upravovat. Editaci lze opět povolit odškrtnutím checkboxu.<br>
          Možnost nastavení cen pro cesťáky je v záložce "Pravidla financování", viz téma  <a href="#" data-topic="club-payment-rules">Pravidla financování</a>.
        </p> 
        
        <a href="${BaseUrl.value}static/help/club_automodules.png" target="_blank"><img src="${BaseUrl.value}static/help/club_automodules.png" alt="ikony akce pro editaci členů oddílu" width="1500px"></a>
       
         
                
    `
  },


  "other-referee-coach": {
    title: "Evidence rozhodčích a trenérů",
    content: `
    <p>
      Evidovat třídy rozhodčích a trenérů může uživatel s pravomocí manažer. Evidence tříd slouží k celkovému přehledu a pro organizátory soutěží, aby věděli, kdo může dělat např. delegáta nebo rozhodčího na soutěži. Přehledy o třídách naleznete na stránce Členové v hlavním menu. Na profilu závodníka také naleznete historii záznamů jeho udělených tříd.<br>
      Manažer může třídy vytvářet a editovat jak na stránce Členové (poslední sloupeček Akce), tak na profilu konkrétního uživatele. Pokud se uživateli prodloužila platnost třídy, pak nevytvářej novou třídu, ale prodluž platnost té stávající. Pokud by dlošlo při vytváření nové třídy ke kolizi se stávající třídou (např. si závodník udělal I. třídu a předtím měl rok starou II. třídu), stará třída se mu automaticky ukončí na den předcházející platnosti nové třídy. 

      <br>
      <a href="${BaseUrl.value}static/help/other_referee_coach.png" target="_blank">
        <img src="${BaseUrl.value}static/help/other_referee_coach.png" alt="" width="1200px">
      </a>
    </p>`
  },
  
  "other-ranking": {
    title: "Národní a oblastní žebříčky",
    content: `
      <p>
        Národní a oblastní žebříčky najdete pod vysouvací záložkou v menu. Nastavení žebříčků (přidávání, odebírání závodů, určování započítávaných kategorií apod.) může uživatel s oprávněním <span style="color:rgb(255, 123, 0)">manažer</span>. Ten také nastavuje počet škrtnutých závodů v jednotlivých žebříčcích za sezónu (každý může nastavit zvlášť). Správce soutěže může na stránce etapy určit pořadatele přes tlačítko "činovníci a organizátoři", kteří dostanou do žebříčku průměr bodů z absolvovaných závodů jako kompenzaci, že si body nemohli v závodě kvůli pořádání vyběhnout (viz pravidla ROB). Průměry se započítávají až od data uskutečnění závodu (i když jsou v ROBisu zapsané už dříve). Škrtnutí nejhorších závodů se započte až po nahrání většího počtu závodů s výsledky, než je počet škrtaných výsledků celkem. Tzn. při 2 škrtaných závodech se toto pravidlo aplikuje, jakmile budou v ROBisu nahrané výsledky ze tří závodů v žebříčku. <br><br>
        Pokud je závod zrušen, resp. kategorie se do žebříčku ze závodu nezapočítává, musí se tato kategorie z žebříčku vyřadit a postiženým závodníkům přidělit body za průměr. Takto přidělený průměr se započítá závodníkům pouze do kategorie, ve které byli v závodě přihlášení. Pokud je závodník v žebříčku veden ve více kategoriích, tak se mu tedy nezapočítá průměr ve všech kategoriích, ale jen v té, ve které závodil. Průměry z pořádání, nebo pokud není závodník v žádné kategorii v závodě přihlášený a přitom má nastavený průměr manažerem v nastavení žebříčku, průměr bodů se mu započítá do všech kategorií, ve kterých je v žebříčku vedený.<br><br>
        Pokud je v etapě více kategorií stejného typu (např. M20 (0-99)), tak se závodníci z těchto kategorií kvůli kolizi nezapočítají do žádného žebříčku. 
      </p>
      <h3>Národní žebříček</h3>
      <p>
        Do národního žebříčku se započítávají soutěže I. stupně. Stupeň (úroveň) soutěže se musí zadat při vytváření soutěže a lze jej měnit v nastavení soutěže v části "Další nastavení soutěže".
      </p>
      <h3>Oblastní žebříčky</h3>
      <p>
        Oblastní žebříčky jsou rozdělené podle oblastí, které odpovídají rozdělení indexů do stejných oblastí. Závod je započítán do určité oblasti a tedy i do oblastního žebříčku podle oblasti oddílu, který je pořadatelem dané soutěže. Do oblastních žebříčků se započítávají soutěže II. a III. stupně. Stupeň (úroveň) soutěže se musí zadat při vytváření soutěže a lze jej měnit v nastavení soutěže v části "Další nastavení soutěže". Do jakéhokoli žebříčku se počítají všichni závodníci s registrací v ČR (AROB). Tzn. zahraniční závodníci se nepočítají.
      </p>
      <h3>Legenda:</h3>
      <li><b>Neuvedená hodnota</b> - závodník se závodu nezúčastnil.</li> 
      <li><b>Šedé zabarvení celého sloupce</b> - soutěž ještě nebyla uzavřena, výsledky a tedy i bodování se může změnit. Soutěž uzavírají její správci nebo se uzavře automaticky 30 dní od data začátku soutěže.</li> 
      <li><b>Přeškrtnutá hodnota</b> - závod je škrtnutý jako nejhorší výsledek. Počet škrtaných výsledků určuje uživatel s oprávněním manažer.</li>
      <li><b>Oranžová hodnota</b> - v daném závodě se pro závodníka započítává průměr z absolvovaných závodů z důvodu organizování v daném závodě. Lze započítat pouze tolik průměrů, kolik závodník absolvoval závodů.</li>
      <li><b>PP</b> - nárok na průměr za pořádání, který se nezapočítal, protože závodník má více nároků na průměr než odběhnutých závodů.</li>
      <li><b>Bodová hodnota je 0</b> - závodník závod absolvoval, ale jeho výsledek je DNF, DNQ, OVT apod.</li>

      <br>
      <a href="${BaseUrl.value}static/help/other_ranking.png" target="_blank">
        <img src="${BaseUrl.value}static/help/other_ranking.png" alt="" width="1500px">
      </a>
    </p>`
  },


  "other-vt": {
    title: "Výkonnostní třídy",
    content: `
      <p>
        ROBis sám umí spočítat, jaké výkonnostní třídy (dále jen VT) závodník v závodě dosáhl a zobrazit aktuální závodníkovu VT dle českých <a href="https://ardf.cz/images_obsah/1579853456-pravidla-rob-2020.pdf" target="_blank">pravidel ROB</a> (příloha C). 
        <br> 
        VT závodníků se spočítá automaticky při nahrávání oficiálních výsledků přes stránku etapy. Pokud došlo ke změnám výsledků nebo do etapy nebyly výsledky nahrány přes stránku etapy, lze VT dopočítat přes tlačítko v nastavení soutěže - "Přepočítat VT". VT se přepočítají pro všechny etapy najednou. 
        <br><br>
        Kvůli pravidlům ROB lze VT spočítat pokud jsou splněny všechny následující podmínky:
        <ul>
          <li>Kategorie, ve které se VT počítá, má definované vyhledávané kontroly. Kontroly lze definovat při nahrávání výsledků nebo v nastavení etapy - "Spravovat kategorie a startovné".</li>
          <li>Kategoriím v nabídce: MDR, Ostatní a ORG se VT nezapočítává. Nejde o oficiální kategorie pro hodnocení.</li>
          <li>V etapě jsou nahrané výsledky a etapa je typu MČR/NŽ, krajská nebo okresní soutěž.</li>
          <li>Závodník má ve výsledku uvedený index ČESKÉHO oddílu.</li>
          <li>Závodník našel všechny povinné kontroly v limitě.</li>
        </ul>
        </p>
        <br>
        <p>
        Uživatel s pravomocí <b>manažer</b> má možnost dopočítat VT jakékoli soutěže přes speciální stránku v ROBisu.
      </p>
      
      `
  },

    "other-global-ranking": {
    title: "Světový ranking",
    content: `
      <p>
         Ranking (nebo také světový ranking) je způsob porovnání závodníků mezi sebou. Z každého závodu získá závodník body, pokud jsou splněny podmínky. Do rankingu se počítá nejlepších 8 hodnocených závodů za posledních 24 měsíců. Body se udělují jen závodníkům, kteří jsou spárovaní s účtem v ROBisu. V kategorii se body počítají pouze tehdy, pokud v ní jsou alespoň <b>tři</b> výsledky se statusem OK. Platí jen kategorie M21, W21, M19, W19.

        <br><br>
        Vzorec pro výpočet počtu bodů za závod:
        <br>
        BZ = (2 − CZ/PC) × PB × (1 − KS × (U − 1)/(N − 1)) × KZ × PK/(CP × 2)
        <ul>
          <li>BZ - body získané závodníkem v závodě</li>
          <li>CZ - čas závodníka</li>
          <li>PC - průměr časů tří nejrychlejších závodníků v kategorii zařazených do rankingu</li>
          <li>PB - průměrná hodnota 3 nejvyšších rankingových čísel závodníků (podle stavu k poslednímu dni měsíce předcházejícího konání závodu), kteří se zúčastnili závodu a mají výsledek OK. Pokud je výsledků 3 a více, ale někteří nejsou součástí rankingu, počítá se jim RČi = 0</li>
          <li>KS - faktor ovlivňující výpočet bodů (zatím neimplementováno, hodnota je 0)</li>
          <li>U - umístění závodníka v závodě</li>
          <li>N - počet hodnocených závodníků v konečných výsledcích</li>
          <li>KZ - koeficient závodu</li>
          <li>PK - Počet správně oražených kontrol závodníkem</li>
          <li>CP - Celkový počet kontrol, které má závodník vyhledat</li>
        </ul>
        </p>
        <br><br>
        <p>
        Vzorec pro výpočet rankingového čísla kvůli dosazení PB: RČi = Bi/Bč4 * 9750 * 8/ZZi (Do června 2026 se počítá pevně 7 000 bodů kvůli zavedení)
        <ul>
          <li>RČi - rankingové číslo i-tého závodníka</li>
          <li>Bi - součet 8 nejlepších výsledků i-tého závodníka</li>
          <li>Bč4 - průměr 4 nejvyšších přepočtených součtů 8 nejlepších výsledků závodníků, v nichž je závodníkům s méně než 8 nenulovými výsledky namísto chybějících závodů započítán průměr</li>
          <li>ZZi - počet započítaných nenulových výsledků i-tého závodníka do Bi (nejvýše 8). Faktor 8/ZZi dorovnává průměr u závodníků s méně než 8 výsledky</li>
        </ul>
        </p>
        <br>
      
      `
  },

  "other-oracon": {
    title: "Radiokontroly - ORACON -> online mezičasy z lesa",
    content: `
      <p>
       Online RAdio CONtrol (ORACON) je zařízení vyvinuté pro odesílání ražení přímo od kontroly Pavlem Kolským. Zařízení odešle do ROBisu data o ražení a ten je zpracuje.<br>
       Správci radiokontrol mohou zařízení i jednotlivá ražení spravovat na speciální stránce - Další → Online kontroly.
       Správci soutěže mohou spravovat pouze ta zařízení, která jim byla správcem online kontrol přidělena, a to ze stránky nastavení soutěže. V tabulce se zobrazí pouze ražení, která souhlasí s aktuálním dnem. Nelze tak upravovat ražení zpětně.<br>
       Nelze odesílat ražení pro uložení do výsledku závodníka, pokud už závod proběhl (datum závodu je v minulosti).<br>
       Uložit změny v zařízení nebo změny v ražení lze pouze po změně některých údajů. Změněný řádek je označen oranžově.
      </p>
      <br>
      <a href="${BaseUrl.value}static/help/other_oracon_tables.png" target="_blank">
        <img src="${BaseUrl.value}static/help/other_oracon_tables.png" alt="" width="1500px">
      </a>
      `
  },

  "other-ochecklist": {
    title: "O-checklist workflow --> přenos změn z aplikace do vyčítacího programu",
    content: `
      <p>
        O-checklist je aplikace pro Android, která slouží k zaznamenání změn údajů o závodnících na startu (např. číslo čipu, čas startu apod.). Data o závodnících s jejich startovními časy lze v ROBisu stáhnout ze: Soutěž → Nastavení → Vyber etapu → Startovka → Export → O-checklist. Exportovaný soubor pak nahrajte do aplikace přes tlačítko Import na úvodní obrazovce. V aplikaci se pak zaznamenávají změny údajů a tyto změny lze jednoduše sdílet na email organizátora v cíli nebo přes internetovou službu.<br><br>  
        ROBis může být prostředníkem mezi aplikací O-checklist a vyčítacím programem v cíli. Celý proces posílání změn údajů závodníků tak lze zautomatizovat a zrychlit. Podmínkou je, že vyčítací program v cíli podporuje stahování právě těchto dat z ROBisu.<br>
        Workflow: V aplikaci O-checklist se nastaví URL adresa na ROBis s identifikátorem. Poslaná data ROBis uloží do databáze. Vyčítací program v cíli si ze stejné adresy stáhne relevantní záznamy (musí se dotazovat na stejnou hodnotu klíče, který posílal O-checklist) a může s nimi dále pracovat. Např. automaticky přepíše dotčenému závodníkovi číslo čipu. Závodník pak po doběhnutí do cíle s novým čipem tento čip vyčte a správně se mu spáruje se jménem, příjmením, indexem apod. Endpoint pro získání dat je popsán <a href="#" data-topic="dev-api">v tématu API</a>.<br>
        <span class="text-red">Důležité: v jednom závodě je potřeba používat stejný klíč<sup>(6)</sup>, protože pod tímto klíčem si i cíl stahuje záznamy. Pokud je závod v ROBisu, používejte ID závodu.</span>
        <h3>Nastavení odesílání dat v O-checklist:</h3>
        <ol class="ml-5">
          <li>Otevřete si nastavení pro POST metodu.<sup>(1,2,3)</sup></li>
          <li>Vyplňte přesně tuhle adresu i s lomítkem na konci.<sup>(4)</sup></li>
          <li>Naište název klíče. Možné je pouze jedno z: "key", "Key", "KEY". Nic jiného nebude fungovat.<sup>(5)</sup></li>
          <li>Vyplňte hodnotu klíče, na které jste se s cílem domluvili. Nejlépe ID etapy, protože to záznam spáruje i se závodem. Používejte primárně ID etapy. Nepoužívejte jednoduché klíče, např. "1", ale třeba "krpaly2025", "pp2025" apod.<sup>(6)</sup></li>
          <li>Klikni na časovač odesílání dat a tím jej aktivuj. Nastav nízký počet minut.<sup>(7)</sup></li>
          <li>Volitelně: Klikni na odesílání pouze neodeslaných změn. ROBis sám ukládá pouze záznamy, které se změnily, ale takhle nebudeš pokaždé posílat všechy změny znovu.<sup>(8)</sup></li>
          <li>Kliknutím na tlačítko otestuj, jestli odesílání funguje.<sup>(9)</sup></li>
        </ol>
      </p>
      <br>
      <a href="${BaseUrl.value}static/help/other_ochecklist_settings1.png" target="_blank">
        <img src="${BaseUrl.value}static/help/other_ochecklist_settings1.png" alt="" width="32%" style="max-width:300px">
      </a>
      <a href="${BaseUrl.value}static/help/other_ochecklist_settings2.png" target="_blank">
        <img src="${BaseUrl.value}static/help/other_ochecklist_settings2.png" alt="" width="32%" style="max-width:300px">
      </a>

      <a href="${BaseUrl.value}static/help/other_ochecklist_settings3.png" target="_blank">
        <img src="${BaseUrl.value}static/help/other_ochecklist_settings3.png" alt="" width="32%" style="max-width:300px">
      </a>

      `
  }
};

// Dynamicky načítá obsah podle zvoleného klíče
const getTopicTitle = computed(() => topicContent[selectedTopicKey.value]?.title || "Nápověda");
const getTopicContent = computed(() => topicContent[selectedTopicKey.value]?.content || '<p>Vyber si téma z menu</p>');

// Zvýrazenění vyhledávaného obsahu
const highlightedContent = computed(() => {
  if (!searchQuery.value) return getTopicContent.value; // Pokud není hledání, vrátí původní obsah

  const query = searchQuery.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escapuje speciální znaky pro RegExp
  const regex = new RegExp(`(${query})`, "gi"); // Case-insensitive hledání
  return getTopicContent.value.replace(regex, '<mark>$1</mark>'); // Zvýrazní hledaný text
});



// Zjistí, zda jsme na mobilu
const isMobile = ref(window.innerWidth < 960);

const updateScreenSize = () => {
  isMobile.value = window.innerWidth < 960;
};


// Zajistí, že prokliky fungují správně
const handleContentLinks = (event) => {
  const topic = event.target.getAttribute("data-topic");
  if (topic) {
    event.preventDefault();
    selectTopic(topic);
  }
};

// Vyhledávač klíčových slov a zobrazení témat, kde se slova nacházejí
const searchQuery = ref("");

const filteredTopics = computed(() => {
  if (!searchQuery.value) return helpTopics.value;

  return helpTopics.value.map(category => {
    const matchingSubtopics = category.subtopics.filter(subtopic => {
      const topicKey = subtopic.key;
      const topic = topicContent[topicKey];
      return topic && (
        topic.title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        topic.content.toLowerCase().includes(searchQuery.value.toLowerCase())
      );
    });

    return matchingSubtopics.length > 0 ? { ...category, subtopics: matchingSubtopics } : null;
  }).filter(Boolean);
});



function showWidget() {
  const widget = document.querySelector('[script-id="5jCC8"]');
  if (widget) {
    widget.style.display = 'block'; // případně 'flex', pokud widget využívá flex
  }
}

function hideWidget() {
  const widget = document.querySelector('[script-id="5jCC8"]');
  if (widget) {
    widget.style.display = 'none';
  }
}



// Při načtení stránky zkontroluj hash a nastav téma:
onMounted(() => {
  showWidget();

  if (route.hash) {
    const key = route.hash.substring(1); // odebere "#"
    if (topicContent[key]) {
      selectTopic(key);
    }
  }
  // Změny velikosti okna
  window.addEventListener("resize", updateScreenSize);
  document.addEventListener("click", handleContentLinks);

  // spuštění chatbota
  // Zkontrolujeme, zda už skript neexistuje

  // Vytvoříme nový script s parametrem pro cache busting
  const scriptElement = document.createElement('script');
  scriptElement.src = `https://static.getbutton.io/widget/bundle.js?id=5jCC8&cb=${Date.now()}`;
  scriptElement.defer = true;
  document.head.appendChild(scriptElement);

});



onUnmounted(() => {
  window.removeEventListener("resize", updateScreenSize);
  document.removeEventListener("click", handleContentLinks);

});


onBeforeUnmount(() => {
  hideWidget();
});


// Watcher, který aktualizuje URL hash při změně tématu:
watch(selectedTopicKey, (newKey) => {
  if (newKey) {
    router.replace(`/napoveda#${newKey}`);
  }
});



// Helpery pro vyhledávání

const isSearching = computed(() => (searchQuery.value || "").trim().length > 0);

function escapeRegExp(s) {
  return (s || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function highlightText(text, q) {
  const query = (q || "").trim();
  if (!query) return text;
  const re = new RegExp(`(${escapeRegExp(query)})`, "gi");
  return (text || "").replace(re, "<mark>$1</mark>");
}

function stripHtml(html) {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return (div.textContent || div.innerText || "").trim();
}

function makeSnippet(plainText, q, radius = 70) {
  const query = (q || "").trim().toLowerCase();
  const text = plainText || "";
  if (!query) return text.slice(0, radius * 2) + (text.length > radius * 2 ? "…" : "");

  const idx = text.toLowerCase().indexOf(query);
  if (idx < 0) return text.slice(0, radius * 2) + (text.length > radius * 2 ? "…" : "");

  const start = Math.max(0, idx - radius);
  const end = Math.min(text.length, idx + query.length + radius);
  const prefix = start > 0 ? "…" : "";
  const suffix = end < text.length ? "…" : "";
  return prefix + text.slice(start, end) + suffix;
}

// výsledky hledání pro pravý panel
const searchResults = computed(() => {
  if (!isSearching.value) return [];

  const q = searchQuery.value.trim();
  const qLower = q.toLowerCase();

  const res = [];
  for (const category of helpTopics.value) {
    for (const subtopic of category.subtopics) {
      const key = subtopic.key;
      const topic = topicContent[key];
      if (!topic) continue;

      const title = topic.title || subtopic.title || key;
      const plain = stripHtml(topic.content || "");

      const match =
        title.toLowerCase().includes(qLower) ||
        plain.toLowerCase().includes(qLower);

      if (!match) continue;

      const snippet = makeSnippet(plain, q);

      res.push({
        key,
        categoryTitle: category.title,
        titleHtml: highlightText(title, q),
        snippetHtml: highlightText(snippet, q),
      });
    }
  }
  return res;
});

// klik na výsledek: otevře téma a ukončí režim hledání
function openSearchResult(key) {
  selectTopic(key);
}


</script>



<style scoped>

::v-deep(ul),
::v-deep(li), 
::v-deep(p) {
  text-align: justify;
  font-size: 15px;
  line-height: 26px;
}

::v-deep([id]:target::before) {
  content: "";
  display: block;
  height: 70px;
  margin-top: -70px;
  visibility: hidden;
}

::v-deep(h3) {
  margin-bottom: 10px;
  margin-top: 20px;
}

::v-deep(ul) {
  padding-left: 20px;
}

::v-deep(.result_csv_table) {
  width: 100%;
  border-collapse: collapse;
}

::v-deep(.result_csv_table th),
::v-deep(.result_csv_table td) {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  text-align: left;
  padding: 4px;
  width: 17%;
  vertical-align: top;
  font-size: 13px;
}

::v-deep(.result_csv_table th) {
  background-color: rgb(var(--v-theme-surface-variant));
  color: rgb(var(--v-theme-on-surface-variant));
  font-weight: bold;
}

.active {
  background-color: rgba(33, 150, 243, 0.2); /* Světle modrá */
  color: #1976d2 !important; /* Hlavní modrá barva Vuetify */
  font-weight: bold;
}

/* Pouze pro mobilní zobrazení */
@media (max-width: 960px) {
  ::v-deep(img) {
    max-width: 100%!important;
    height: auto!important; /* Obrázky budou max. tak široké jako obrazovka */
  }
}

::v-deep(mark) {
  background-color: yellow;
  color: black;
  font-weight: bold;
}

::v-deep(code) {
  background-color: rgb(var(--v-theme-code));
  color: rgb(var(--v-theme-on-code));
  padding: 2px 4px;
  border-radius: 4px;
}

/* Rámeček kolem obrázku */
/* ::v-deep(img) {
  border: 1px solid #c2d5ff;
  border-radius: 4px;
  padding: 2px;
  } */

</style>
